import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  companyHasProfile,
  companyHasAnyProfile,
} from "../utils/activityProfiles";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Button,
  Upload,
  message,
  Space,
  Pagination,
  InputNumber,
  Spin,
  Alert,
  Tooltip,
  Typography,
  Dropdown,
  Menu,
  Tag,
} from "antd";
import Barcode from "react-barcode";
import {
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ImportOutlined,
  UploadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  DownOutlined,
  WarningOutlined,
  InboxOutlined,
  StopOutlined,
  UndoOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { isCompanySuspendedError } from "../utils/companySuspensionNotice.js";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import ProductCatalogPackModal from "./ProductCatalogPackModal";
import ProductDetailsModal from "./ProductDetailsModal";
import ProductEditorModal from "./ProductEditorModal";
import useProductFormOptions, { getProductReferenceConflicts } from "./useProductFormOptions";
import "./styles.css";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import API_CONFIG from '../config/api.js';
import VariantManager, {
  buildVariantAttributes,
} from "./VariantManager.jsx";
import VariantManagerModal from "./VariantManagerModal.jsx";
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import DependencyList from '../components/DependencyList';
import ProductImageGallery from './ProductImageGallery';
import ProductStatusBadge from '../components/ProductStatusBadge';
import RowActionsMenu from "../components/RowActionsMenu";
import { getSelectedProductionTerminology } from "../GestionProductions/productionTerminology";

const { Option } = Select;
const { Dragger } = Upload;
const { Title, Text, Link } = Typography;
const API_URL = API_CONFIG.BASE_URL;

// --- Helper function to get image URL (simplified - trust backend) ---
const getProductImageUrl = (imagePath) => {
  // Backend normalizes all URLs - just return what we get
  return imagePath || undefined;
};
// --- End Helper ---

// Fonction utilitaire pour formater les nombres
const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(value);

const isInteractiveRowElement = (target) => {
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest(
      [
        "button",
        "a",
        "input",
        "textarea",
        "select",
        "[role='button']",
        "[role='menuitem']",
        ".ant-btn",
        ".ant-dropdown-trigger",
        ".ant-dropdown-menu-item",
        ".ant-select",
        ".ant-switch",
        ".ant-checkbox-wrapper",
        ".ant-radio-wrapper",
        ".ant-popover",
        ".ant-tooltip",
      ].join(", ")
    )
  );
};

const formatPriceForInput = (value, allowDecimal = false) => {
  if (value === null || value === undefined || value === "") return "";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "";

  return `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: allowDecimal ? 4 : 0,
    useGrouping: true,
  }).format(numericValue)} CFA`;
};

const formatQuantityForInput = (value, allowDecimal = false) => {
  if (value === null || value === undefined || value === "") return "";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "";

  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: allowDecimal ? 4 : 0,
    useGrouping: true,
  }).format(numericValue);
};

const parsePriceInput = (value, allowDecimal = false) => {
  const rawValue = String(value ?? "");

  if (!allowDecimal) {
    const parsed = rawValue.replace(/[^\d]/g, "");
    return parsed ? Number(parsed) : "";
  }

  const normalized = rawValue.replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return "";

  const [integerPart, ...decimalParts] = normalized.split(".");
  const decimalPart = decimalParts.join("").slice(0, 4);
  const normalizedNumber = decimalPart
    ? `${integerPart}.${decimalPart}`
    : integerPart;

  const parsed = Number(normalizedNumber);
  return Number.isNaN(parsed) ? "" : parsed;
};

const parseQuantityInput = (value, allowDecimal = false) =>
  parsePriceInput(value, allowDecimal);

const normalizePositiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
};

const formatAssistantValue = (value, options = {}) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return "0";

  const { minimumFractionDigits = 0, maximumFractionDigits = 4 } = options;

  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: true,
  }).format(numericValue);
};

const createPurchasePackagingProfileDraft = (overrides = {}) => ({
  client_key:
    overrides.client_key ||
    `packaging-${Date.now()}-${Math.round(Math.random() * 100000)}`,
  id: overrides.id ?? null,
  // Ne pas trim ici : sinon l'input controle supprime les espaces
  // intermediaires/trailing pendant la frappe. Le trim doit rester au moment
  // de comparer ou de persister les profils.
  label: overrides.label == null ? "" : String(overrides.label),
  contained_quantity: normalizePositiveNumber(overrides.contained_quantity),
  reference_purchase_price: normalizePositiveNumber(
    overrides.reference_purchase_price
  ),
  is_default:
    overrides.is_default === true ||
    overrides.is_default === 1 ||
    overrides.is_default === "1",
});

const normalizePurchasePackagingProfilesState = (profiles = []) => {
  const normalizedProfiles = (Array.isArray(profiles) ? profiles : []).map(
    (profile) => createPurchasePackagingProfileDraft(profile)
  );

  if (normalizedProfiles.length === 0) {
    return [];
  }

  let defaultIndex = normalizedProfiles.findIndex((profile) => profile.is_default);
  if (defaultIndex < 0) {
    defaultIndex = 0;
  }

  return normalizedProfiles.map((profile, index) => ({
    ...profile,
    is_default: index === defaultIndex,
  }));
};

const buildPurchasePackagingProfilesPayload = (profiles = []) =>
  normalizePurchasePackagingProfilesState(profiles)
    .filter((profile) => {
      const hasMeaningfulValue =
        String(profile.label || "").trim() ||
        normalizePositiveNumber(profile.contained_quantity) > 0 ||
        normalizePositiveNumber(profile.reference_purchase_price) > 0;

      return Boolean(hasMeaningfulValue);
    })
    .map((profile) => ({
      id: profile.id ?? undefined,
      label: String(profile.label || "").trim(),
      contained_quantity: normalizePositiveNumber(profile.contained_quantity),
      reference_purchase_price: normalizePositiveNumber(
        profile.reference_purchase_price
      ),
      is_default: Boolean(profile.is_default),
    }))
    .filter(
      (profile) =>
        profile.label &&
        normalizePositiveNumber(profile.contained_quantity) > 0
    );

const normalizeProductionUsageProfiles = (profiles = []) =>
  (Array.isArray(profiles) ? profiles : []).map((profile) => ({
    id: profile?.id ?? null,
    label: String(profile?.label || "").trim(),
    production_unit_id: profile?.production_unit_id ?? null,
    production_unit_name:
      profile?.source_production_unit_name ||
      profile?.production_unit_name ||
      "",
    reference_output_quantity: normalizePositiveNumber(
      profile?.reference_output_quantity
    ),
    reference_output_unit_short_name:
      profile?.reference_output_unit_short_name || "unite",
    material_quantity: normalizePositiveNumber(profile?.material_quantity),
    material_unit_short_name: profile?.material_unit_short_name || "unite",
    loss_rate: normalizePositiveNumber(profile?.loss_rate),
    yield_rate: normalizePositiveNumber(profile?.yield_rate),
  }));

const normalizeSelectionId = (selection) => selection?.id ?? selection ?? null;

const PRODUCT_FORM_FIELD_TAB_MAP = {
  warehouse: "1",
  product_type: "1",
  nom_produit: "1",
  item_code: "1",
  barcode_value: "1",
  id_categorie: "1",
  slug: "1",
  description: "1",
  barcode_symbology: "1",
  quantite_stock: "2",
  id_unit: "2",
  alerte_quantite: "2",
  prix_achat: "2",
  prix_vente: "2",
  taxe: "2",
};

const getProductTypeMeta = (productType) => {
  switch (String(productType || "").toLowerCase()) {
    case "raw":
      return { label: "Matière première", color: "gold" };
    case "production":
      return { label: "Unité de production", color: "geekblue" };
    case "single":
      return { label: "Produits finis", color: "green" };
    default:
      return { label: productType || "-", color: "default" };
  }
};

const GestionProduit = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productSubmitting, setProductSubmitting] = useState(false);
  const productSubmitLockRef = useRef(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  // Assistant inline (catalogue secteur) : suggestions de produits standards.
  const [productCatalogSuggestions, setProductCatalogSuggestions] = useState([]);
  const [loadingCatalogSuggestions, setLoadingCatalogSuggestions] = useState(false);
  const catalogSuggestTimerRef = useRef(null);
  const [form] = Form.useForm();
  const productEditorRef = useRef(null);
  const productEditRequestRef = useRef(0);
  const [quickCategoryForm] = Form.useForm();
  const [quickUnitForm] = Form.useForm();
  const selectedProductType = Form.useWatch("product_type", form);
  const watchedProductName = Form.useWatch("nom_produit", form);
  const watchedStockUnitId = Form.useWatch("id_unit", form);
  const watchedFormWarehouseId = Form.useWatch("warehouse", form);
  const watchedCategoryId = Form.useWatch("id_categorie", form);
  const watchedTaxId = Form.useWatch("taxe", form);
  const watchedPurchasePrice = Form.useWatch("prix_achat", form);
  const watchedOpeningStockQuantity = Form.useWatch("quantite_stock", form);
  const watchedAlertQuantity = Form.useWatch("alerte_quantite", form);
  const assistantPackagingLabel = Form.useWatch("assistant_packaging_label", form);
  const assistantPackagingQuantity = Form.useWatch(
    "assistant_packaging_quantity",
    form
  );
  const assistantPackagingPurchasePrice = Form.useWatch(
    "assistant_packaging_purchase_price",
    form
  );
  const assistantOpeningPackageCount = Form.useWatch(
    "assistant_opening_package_count",
    form
  );
  const assistantAlertPackageCount = Form.useWatch(
    "assistant_alert_package_count",
    form
  );
  const isRawMaterialType = selectedProductType === "Matières premières";
  const previousProductTypeRef = useRef();
  const [barcodeGenerated, setBarcodeGenerated] = useState(false);
  const [currentBarcode, setCurrentBarcode] = useState(null);
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const resetBarcodeState = useCallback(() => {
    setBarcodeGenerated(false);
    setCurrentBarcode(null);
    setBarcodeModalVisible(false);
  }, []);
  const [categories, setCategories] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [formUnits, setFormUnits] = useState([]);
  const [formSelectedWarehouseCompanyId, setFormSelectedWarehouseCompanyId] =
    useState(null);
  const [quickCategoryModalVisible, setQuickCategoryModalVisible] =
    useState(false);
  const [quickUnitModalVisible, setQuickUnitModalVisible] = useState(false);
  const [quickCategoryLoading, setQuickCategoryLoading] = useState(false);
  const [quickUnitLoading, setQuickUnitLoading] = useState(false);
  const [purchasePackagingProfiles, setPurchasePackagingProfiles] = useState([]);
  const [productionUsageProfiles, setProductionUsageProfiles] = useState([]);
  const [loadingProductionUsageProfiles, setLoadingProductionUsageProfiles] =
    useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [activeProductFormTab, setActiveProductFormTab] = useState("1");
  const [filters, setFilters] = useState({
    search: "",
    categorie: "",
    warehouse: "",
    type: "",
    status: "all",
  });

  // --- State for Import Modal ---
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [importResult, setImportResult] = useState(null);
  // --- End State for Import Modal ---

  const [exporting, setExporting] = useState(false);

  // États pour la suppression sécurisée
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedProductForAction, setSelectedProductForAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Note: statusFilter supprimé - on utilise filters.status directement

  // États pour la gestion des produits supprimés (restauration/remplacement)
  const [deletedProductInfo, setDeletedProductInfo] = useState(null);
  const [deletedProductModalVisible, setDeletedProductModalVisible] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null); // FormData en attente

  // Extraction du magasin sélectionné (et son setter) depuis le contexte global
  const { selectedCompany, selectedWarehouse, setSelectedWarehouse } =
    useSelection();
  const { user, isLoading: authLoading, hasPermission } = useAuth();
  const invalidateProducts = useBusinessMutationInvalidation("product");
  const invalidateCategories = useBusinessMutationInvalidation("product-category");
  const invalidateUnits = useBusinessMutationInvalidation("unit");
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );
  const subscriptionAccessCompanyId = Number(
    user?.subscription_access?.company_id,
  );
  const selectedSubscriptionCompanyId = Number(selectedCompany);
  const subscriptionAccessMatchesSelection =
    !Number.isSafeInteger(selectedSubscriptionCompanyId) ||
    selectedSubscriptionCompanyId <= 0 ||
    !Number.isSafeInteger(subscriptionAccessCompanyId) ||
    subscriptionAccessCompanyId === selectedSubscriptionCompanyId;
  const subscriptionWriteRestricted = Boolean(
    Number(user?.is_superadmin) !== 1 &&
      subscriptionAccessMatchesSelection &&
      user?.subscription_access?.restriction_enforced &&
      ["read_only", "suspended"].includes(
        user?.subscription_access?.access_state,
      ),
  );
  const subscriptionRestrictionTitle =
    "Action indisponible : la souscription autorise uniquement la consultation.";
  // Pack catalogue produits : actif uniquement pour les profils restauration.
  const [catalogPackVisible, setCatalogPackVisible] = useState(false);
  const isCatalogPackEligible = companyHasAnyProfile(user, [
    "restaurant",
    "fast_food",
    "bakery",
  ]);

  // Produits a variantes (declinaisons Taille x Couleur) : reserve au profil e-commerce.
  // Le mode variantes est integre AU formulaire produit standard via un toggle :
  // - variantMode  : true => le produit cree est un parent + N declinaisons
  // - variantAxes  : [{ name, values: [] }] axes de declinaison
  // - variantRows  : declinaisons generees (chaque ligne = 1 produit enfant)
  const [variantMode, setVariantMode] = useState(false);
  const [variantAxes, setVariantAxes] = useState([
    { name: "Taille", values: [] },
  ]);
  const [variantRows, setVariantRows] = useState([]);
  // En conversion, identifie la combinaison qui reutilise le produit existant
  // (et conserve donc son ID, son stock, ses codes et son historique).
  const [existingVariantKey, setExistingVariantKey] = useState(null);
  // Cache des declinaisons chargees a l'expansion d'une ligne parent dans la liste.
  const [variantChildrenMap, setVariantChildrenMap] = useState({});
  const [loadingVariantChildren, setLoadingVariantChildren] = useState({});
  // Modal "Gerer les declinaisons" (edition parent + enfants). Stocke l'id du parent.
  const [manageVariantsParentId, setManageVariantsParentId] = useState(null);
  const isVariantsEligible = companyHasProfile(user, "ecommerce");
  const canConfigureVariants =
    isVariantsEligible &&
    (!editingProduct ||
      (String(editingProduct.product_type || "").toLowerCase() === "single" &&
        !Boolean(editingProduct.is_variant_parent) &&
        !editingProduct.parent_product_id));

  useEffect(() => {
    if (
      existingVariantKey &&
      !variantRows.some((variant) => variant.key === existingVariantKey)
    ) {
      setExistingVariantKey(null);
    }
  }, [existingVariantKey, variantRows]);

  // Assistant inline : suggere des produits standards du catalogue secteur
  // d'apres le nom saisi (creation uniquement, profils restauration). Debounce.
  useEffect(() => {
    if (!modalVisible || editingProduct || !isCatalogPackEligible) {
      setProductCatalogSuggestions([]);
      return undefined;
    }
    const name = String(watchedProductName || "").trim();
    if (name.length < 2) {
      setProductCatalogSuggestions([]);
      return undefined;
    }
    if (catalogSuggestTimerRef.current) {
      clearTimeout(catalogSuggestTimerRef.current);
    }
    catalogSuggestTimerRef.current = setTimeout(async () => {
      setLoadingCatalogSuggestions(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/api/production/onboarding/products/suggestions`,
          {
            params: {
              name,
              warehouse_id: selectedWarehouse || undefined,
              company_id: normalizeSelectionId(selectedCompany) || undefined,
            },
          }
        );
        setProductCatalogSuggestions(
          data && data.eligible && Array.isArray(data.suggestions)
            ? data.suggestions
            : []
        );
      } catch (error) {
        setProductCatalogSuggestions([]);
      } finally {
        setLoadingCatalogSuggestions(false);
      }
    }, 450);

    return () => {
      if (catalogSuggestTimerRef.current) {
        clearTimeout(catalogSuggestTimerRef.current);
      }
    };
  }, [
    modalVisible,
    editingProduct,
    isCatalogPackEligible,
    watchedProductName,
    selectedWarehouse,
    selectedCompany,
  ]);

  const applyCatalogProductSuggestion = (suggestion) => {
    if (!suggestion) return;
    form.setFieldsValue({
      nom_produit: suggestion.name,
      slug: generateSlug(suggestion.name),
      item_code: generateArticleCode(suggestion.name),
      product_type:
        suggestion.product_type === "single"
          ? "Produits finis"
          : "Matières premières",
      id_categorie: suggestion.category_id || undefined,
      id_unit: suggestion.unit_id || undefined,
      prix_achat: Number(suggestion.purchase_price) || 0,
      prix_vente: Number(suggestion.sales_price) || 0,
    });
    setProductCatalogSuggestions([]);
    message.success(`Champs pré-remplis depuis « ${suggestion.name} ».`);
  };
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCompanyId = normalizeSelectionId(selectedCompany);
  const assistantSearchParams = new URLSearchParams(location.search);
  const productionAssistantMode =
    assistantSearchParams.get("assistant") === "production-setup";
  const productionAssistantAction = String(
    assistantSearchParams.get("action") || ""
  ).trim();
  const isProductionAssistantRawMaterialFlow =
    productionAssistantMode &&
    productionAssistantAction === "create-raw-material";
  const assistantWarehouseId =
    Number(assistantSearchParams.get("warehouse")) || selectedWarehouse || null;
  const availableProductWarehouses = (user?.assigned_warehouses || []).filter(
    (warehouse) =>
      !selectedCompanyId || warehouse.company_id === selectedCompanyId
  );
  const selectedProductFormWarehouse = availableProductWarehouses.find(
    (warehouse) =>
      normalizeSelectionId(warehouse.id) === normalizeSelectionId(selectedWarehouse)
  );
  const productContextKey = JSON.stringify([user?.id, selectedCompanyId, normalizeSelectionId(selectedWarehouse)]);
  const productContextRef = useRef({ key: productContextKey, open: modalVisible });
  productContextRef.current = { key: productContextKey, open: modalVisible };
  const previousProductContextRef = useRef(productContextKey);
  const productFormOptions = useProductFormOptions({ userId: user?.id, companyId: selectedCompanyId,
    warehouseId: watchedFormWarehouseId || normalizeSelectionId(selectedWarehouse), open: modalVisible });
  const formOptionData = productFormOptions.data;
  const productReferenceConflicts = getProductReferenceConflicts({ warehouse: watchedFormWarehouseId,
    id_categorie: watchedCategoryId, id_unit: watchedStockUnitId, taxe: watchedTaxId }, formOptionData);
  const productDraftRef = useRef("");
  productDraftRef.current = JSON.stringify({ variantRows, variantAxes, existingVariantKey, purchasePackagingProfiles });
  useEffect(() => {
    if (!formOptionData) return;
    setFormUnits(formOptionData.units);
    setCategories(formOptionData.categories);
    setTaxes(formOptionData.taxes);
    setWarehouses(formOptionData.warehouses);
  }, [formOptionData]);
  const assistantLaunchHandledRef = useRef(false);
  const assistantPrefillHandledRef = useRef(false);
  const productionAssistantCreateMode =
    isProductionAssistantRawMaterialFlow && !editingProduct;
  const assistantStockUnit =
    formUnits.find((unit) => unit.id === watchedStockUnitId) ||
    allUnits.find((unit) => unit.id === watchedStockUnitId) ||
    null;
  const assistantStockUnitShortName =
    assistantStockUnit?.short_name || "unite";
  const assistantStockUnitLabel = assistantStockUnit
    ? `${assistantStockUnit.name} (${assistantStockUnit.short_name})`
    : "Aucune unite de stock selectionnee";
  const assistantConditioningName = String(
    assistantPackagingLabel || ""
  ).trim();
  const assistantConditioningDisplayName =
    assistantConditioningName || "Conditionnement";
  const assistantPackagingQuantityValue = normalizePositiveNumber(
    assistantPackagingQuantity
  );
  const assistantPackagingPurchasePriceValue = normalizePositiveNumber(
    assistantPackagingPurchasePrice
  );
  const assistantOpeningPackageCountValue = normalizePositiveNumber(
    assistantOpeningPackageCount
  );
  const assistantAlertPackageCountValue = normalizePositiveNumber(
    assistantAlertPackageCount
  );
  const assistantDerivedUnitPurchasePrice =
    assistantPackagingQuantityValue > 0
      ? assistantPackagingPurchasePriceValue / assistantPackagingQuantityValue
      : 0;
  const assistantDerivedOpeningStock =
    assistantPackagingQuantityValue * assistantOpeningPackageCountValue;
  const assistantDerivedAlertQuantity =
    assistantPackagingQuantityValue * assistantAlertPackageCountValue;
  const assistantCanApplyUnitPurchasePrice =
    Boolean(assistantStockUnit) &&
    assistantPackagingQuantityValue > 0 &&
    assistantPackagingPurchasePriceValue > 0;
  const assistantCanApplyOpeningStock =
    Boolean(assistantStockUnit) &&
    assistantPackagingQuantityValue > 0 &&
    assistantOpeningPackageCountValue > 0;
  const assistantCanApplyAlertQuantity =
    Boolean(assistantStockUnit) &&
    assistantPackagingQuantityValue > 0 &&
    assistantAlertPackageCountValue > 0;

  // Nouvel état pour gérer la stabilité des sélections
  const canSaveAssistantPackagingProfile =
    Boolean(assistantStockUnit) &&
    assistantPackagingQuantityValue > 0 &&
    Boolean(assistantConditioningName);
  const showPurchasePackagingProfilesSection =
    isRawMaterialType ||
    purchasePackagingProfiles.length > 0 ||
    productionAssistantCreateMode;
  const showProductionUsageProfilesSection =
    Boolean(editingProduct?.id) &&
    (editingProduct?.product_type === "raw" || isRawMaterialType);
  const [selectionsReady, setSelectionsReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // --- DEBUGGING PERMISSIONS ---
  useEffect(() => {
    if (user && user.permissions) {
    } else {
    }
  }, [user]);
  // --- END DEBUGGING ---

  // Surveiller la stabilité des sélections AVANT de charger les produits
  useEffect(() => {
    // Vérifier si l'initialisation est terminée
    const initializationComplete =
      !authLoading && // L'authentification est terminée
      user && // L'utilisateur est chargé
      selectedCompany !== undefined && // Une entreprise est définie (peut être null)
      selectedWarehouse !== undefined; // Le warehouse est défini (peut être null)

    // Vérifier si on peut afficher les données (magasin spécifique requis)
    const canShowData =
      initializationComplete &&
      selectedCompany && // Une entreprise est sélectionnée (requis)
      selectedWarehouse; // Un magasin spécifique est sélectionné (requis)

    setIsInitializing(!initializationComplete);
    setSelectionsReady(canShowData);
  }, [authLoading, user, selectedCompany, selectedWarehouse]);

  // --- Gestion du filtre "categorie" (schéma existant) ---
  const handleCategoryFilter = (value) => {
    setFilters((prev) => ({ ...prev, categorie: value }));
  };

  // --- Gestion du filtre "type" ---
  // On traite la valeur en tant que nombre pour éviter les problèmes de type
  const handleProductTypeFilter = (value) => {
    setFilters((prev) => ({ ...prev, type: value || "" }));
  };

  // useEffect qui surveille le changement de selectedWarehouse dans le contexte
  useEffect(() => {
    // Met à jour le filtre local "warehouse" (on s'assure d'avoir un nombre ou une chaîne vide)
    setFilters((prev) => ({ ...prev, warehouse: selectedWarehouse || "" }));
    // Ne pas appeler fetchProducts ici, il sera déclenché par le changement de filters
  }, [selectedWarehouse]);

  // --- Génération du code-barres, slug et article code ---
  const generateBarcode = async () => {
    try {
      const type = form.getFieldValue("barcode_symbology");
      if (!type) {
        message.error("Veuillez d'abord sélectionner un type de code-barres");
        return;
      }
      const response = await axios.get(
        `${API_URL}/api/produits/generate-barcode`,
        {
          params: { type },
        }
      );
      const nextBarcodeValue = response.data?.barcode;
      if (!nextBarcodeValue) {
        throw new Error("Aucun code-barres généré");
      }
      form.setFieldsValue({ barcode_value: nextBarcodeValue });
      setCurrentBarcode(String(nextBarcodeValue));
      setBarcodeGenerated(true);
      setBarcodeModalVisible(false);
      message.success("Code-barres généré avec succès");
    } catch (error) {
      message.error("Erreur lors de la génération du code-barres");
      console.error("[generateBarcode] Erreur :", error);
    }
  };

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const generateArticleCode = (name) => {
    const cleanName = name
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Z0-9]/g, "");
    const prefix = cleanName.slice(0, 3);
    const timestamp = Date.now().toString().slice(-5);
    return `${prefix}${timestamp}`;
  };

  // --- Récupération des catégories ---
  const fetchCategories = async () => {
    try {
      const params = selectedCompanyId
        ? { company_id: selectedCompanyId }
        : {};
      const response = await axios.get(`${API_URL}/api/categories`, { params });
      if (response.data) setCategories(response.data);
    } catch (error) {
      if (!isCompanySuspendedError(error)) {
        message.error("Erreur lors du chargement des catégories");
        console.error("[fetchCategories] Erreur :", error);
      }
    }
  };

  // --- Affichage et impression du code-barres ---
  const showBarcode = () => {
    const currentBarcodeValue = form.getFieldValue("barcode_value");
    if (currentBarcodeValue) {
      setCurrentBarcode(currentBarcodeValue);
      setBarcodeModalVisible(true);
    } else {
      message.error("Aucun code-barres disponible");
    }
  };

  const printBarcode = () => {
    const barcodeSvg = document.querySelector("#barcode-print svg");
    if (!barcodeSvg) {
      message.error("Aucun code-barres disponible à imprimer");
      return;
    }
    // Open synchronously from the click so the browser can authorise the print window.
    const printWindow = window.open("", "_blank", "width=700,height=500");
    if (!printWindow) {
      message.error("La fenêtre d’impression a été bloquée. Autorisez les fenêtres contextuelles pour imprimer.");
      return;
    }
    try {
      printWindow.opener = null;
      const printDocument = printWindow.document;
      printDocument.open();
      // Static markup only: the barcode and title are copied through DOM APIs.
      printDocument.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8">
        <title>Code-barres</title><style>
          @page { size: 50mm 30mm; margin: 0; }
          * { box-sizing: border-box; }
          html, body { margin: 0; background: #fff; color: #000; }
          body { width: 50mm; height: 30mm; padding: 2mm; display: flex;
            align-items: center; justify-content: center;
            print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          svg { display: block; width: 100%; height: auto; max-height: 100%; }
        </style></head><body></body></html>`);
      printDocument.title = `Code-barres — ${currentBarcode}`;
      printDocument.body.appendChild(printDocument.importNode(barcodeSvg, true));
      printDocument.close();
      printWindow.addEventListener("afterprint", () => printWindow.close(), { once: true });
      printWindow.focus();
      printWindow.print();
    } catch (error) {
      printWindow.close();
      message.error("Erreur lors de l’impression du code-barres");
      console.error("[printBarcode] Erreur :", error);
    }
  };

  // --- Récupération des autres données ---
  useEffect(() => {
    fetchTaxes();
    fetchAllUnits();
  }, []);

  // --- Récupération des catégories basées sur l'entreprise sélectionnée ---
  useEffect(() => {
    if (selectedCompanyId) {
      fetchCategories();
    } else {
      setCategories([]); // Vider les catégories si aucune entreprise n'est sélectionnée
    }
  }, [selectedCompanyId]);

  // Appel de fetchProducts lors des changements de pagination ou de filtres
  useEffect(() => {
    // Ne charger les produits que si les sélections sont stables
    if (selectionsReady) {
      fetchProducts();
    }
  }, [pagination.current, pagination.pageSize, filters, selectionsReady]);

  useEffect(() => {}, [products]);

  useEffect(() => {
    if (!modalVisible) {
      previousProductTypeRef.current = selectedProductType;
      return;
    }

    const previousType = previousProductTypeRef.current;

    if (
      !editingProduct &&
      selectedProductType === "Matières premières" &&
      previousType !== "Matières premières"
    ) {
      const purchasePrice = form.getFieldValue("prix_achat");
      form.setFieldsValue({ prix_vente: purchasePrice ?? 0 });
    }

    previousProductTypeRef.current = selectedProductType;
  }, [editingProduct, form, modalVisible, selectedProductType]);

  useEffect(() => {
    if (!modalVisible || editingProduct) {
      return;
    }

    const currentFormWarehouseId = form.getFieldValue("warehouse");
    const currentWarehouse = availableProductWarehouses.find(
      (warehouse) =>
        normalizeSelectionId(warehouse.id) ===
        normalizeSelectionId(currentFormWarehouseId)
    );

    // A removed reference is a conflict; never replace an in-progress selection.
    if (currentFormWarehouseId && !currentWarehouse) return;

    if (currentWarehouse) {
      if (formSelectedWarehouseCompanyId !== currentWarehouse.company_id) {
        setFormSelectedWarehouseCompanyId(currentWarehouse.company_id || null);
      }
      return;
    }

    const defaultWarehouse =
      selectedProductFormWarehouse ||
      (availableProductWarehouses.length === 1
        ? availableProductWarehouses[0]
        : null);

    if (!defaultWarehouse) {
      return;
    }

    if (currentFormWarehouseId !== defaultWarehouse.id) {
      form.setFieldsValue({ warehouse: defaultWarehouse.id });
    }

    if (formSelectedWarehouseCompanyId !== defaultWarehouse.company_id) {
      setFormSelectedWarehouseCompanyId(defaultWarehouse.company_id || null);
    }
  }, [
    availableProductWarehouses,
    editingProduct,
    form,
    formSelectedWarehouseCompanyId,
    modalVisible,
    selectedProductFormWarehouse,
  ]);

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  };

  const getProductFormTabByField = useCallback(
    (fieldPath) => {
      const fieldName = Array.isArray(fieldPath) ? fieldPath[0] : fieldPath;
      return PRODUCT_FORM_FIELD_TAB_MAP[fieldName] || "1";
    },
    []
  );

  const focusProductField = useCallback(
    (fieldPath) => {
      const normalizedFieldPath = Array.isArray(fieldPath)
        ? fieldPath
        : [fieldPath];
      const fieldName = normalizedFieldPath[0];
      productEditorRef.current?.revealField(fieldName);
      const targetTab = getProductFormTabByField(normalizedFieldPath);

      if (targetTab && targetTab !== activeProductFormTab) {
        setActiveProductFormTab(targetTab);
      }

      setTimeout(() => {
        form.scrollToField(normalizedFieldPath, {
          behavior: "smooth",
          block: "center",
        });

        const fieldInstance = form.getFieldInstance?.(normalizedFieldPath);
        if (fieldInstance?.focus) {
          fieldInstance.focus();
          return;
        }

        const domField =
          document.querySelector(`[id="${fieldName}"]`) ||
          document.querySelector(`[id$="_${fieldName}"]`) ||
          document.querySelector(`[name="${fieldName}"]`);

        const focusTarget =
          domField?.querySelector?.(
            "input,textarea,.ant-select-selection-search-input,.ant-input-number-input"
          ) || domField;

        if (focusTarget?.focus) {
          focusTarget.focus();
        }
      }, 140);
    },
    [activeProductFormTab, form, getProductFormTabByField]
  );

  const handleProductFormFinishFailed = useCallback(
    (errorInfo) => {
      const firstError = errorInfo?.errorFields?.[0];
      const firstField = firstError?.name;
      const firstErrorMessage =
        firstError?.errors?.[0] ||
        "Veuillez corriger les informations du formulaire.";

      if (firstField?.length) {
        focusProductField(firstField);
      }

      message.error(firstErrorMessage);
    },
    [focusProductField]
  );

  // Reinitialise le mode variantes (axes + declinaisons) a son etat de depart.
  const resetVariantState = useCallback(() => {
    setVariantMode(false);
    setVariantAxes([{ name: "Taille", values: [] }]);
    setVariantRows([]);
    setExistingVariantKey(null);
  }, []);

  const handleModalClose = useCallback(() => {
    productEditRequestRef.current += 1;
    setModalVisible(false);
    setEditingProduct(null);
    setPurchasePackagingProfiles([]);
    setProductionUsageProfiles([]);
    resetBarcodeState();
    setActiveProductFormTab("1");
    setFormSelectedWarehouseCompanyId(null);
    setQuickCategoryModalVisible(false);
    setQuickUnitModalVisible(false);
    quickCategoryForm.resetFields();
    quickUnitForm.resetFields();
    form.resetFields();
    resetVariantState();
  }, [form, quickCategoryForm, quickUnitForm, resetBarcodeState, resetVariantState]);

  useEffect(() => {
    if (previousProductContextRef.current !== productContextKey && modalVisible) {
      handleModalClose();
    }
    previousProductContextRef.current = productContextKey;
  }, [handleModalClose, modalVisible, productContextKey]);

  // Bascule du toggle "Produit a variantes". A l'extinction, on revient sur
  // l'onglet generique pour eviter de rester sur un onglet "Declinaisons" masque.
  const handleToggleVariantMode = useCallback(
    (checked) => {
      setVariantMode(checked);
      if (checked) {
        form.setFieldValue("product_type", "Produits finis");
      }
      if (!checked) {
        setVariantAxes([{ name: "Taille", values: [] }]);
        setVariantRows([]);
        setExistingVariantKey(null);
        setActiveProductFormTab((tab) => (tab === "variants" ? "1" : tab));
      }
    },
    [form]
  );

  // --- MODIFIED: handleImport to open the modal ---
  const handleImport = () => {
    setImportResult(null);
    setFileList([]);
    setImportModalVisible(true);
  };
  // --- END MODIFIED ---

  // --- Récupération des entrepôts ---
  const fetchWarehouses = async () => {
    try {
      const params = selectedCompanyId
        ? { company_id: selectedCompanyId }
        : {};
      const response = await axios.get(`${API_URL}/api/warehouses`, { params });
      if (response.data && response.data.warehouses) {
        setWarehouses(response.data.warehouses);
      } else {
        setWarehouses([]);
      }
    } catch (error) {
      if (!isCompanySuspendedError(error)) {
        message.error("Erreur lors du chargement des établissements");
        console.error("[fetchWarehouses] Erreur :", error);
      }
    }
  };

  const handleOpenCreateProductModal = useCallback(() => {
    setEditingProduct(null);
    setPurchasePackagingProfiles([]);
    setProductionUsageProfiles([]);
    resetBarcodeState();
    resetVariantState();
    form.resetFields();
    const defaultWarehouse =
      selectedProductFormWarehouse ||
      (availableProductWarehouses.length === 1
        ? availableProductWarehouses[0]
        : null);

    if (defaultWarehouse) {
      form.setFieldsValue({ warehouse: defaultWarehouse.id });
      setFormSelectedWarehouseCompanyId(defaultWarehouse.company_id || null);
    } else {
      setFormSelectedWarehouseCompanyId(null);
    }

    setActiveProductFormTab("1");
    setModalVisible(true);
  }, [
    availableProductWarehouses,
    form,
    resetBarcodeState,
    resetVariantState,
    selectedProductFormWarehouse,
  ]);

  useEffect(() => {
    if (
      !productionAssistantMode ||
      productionAssistantAction !== "create-raw-material"
    ) {
      assistantLaunchHandledRef.current = false;
      assistantPrefillHandledRef.current = false;
      return;
    }

    if (assistantLaunchHandledRef.current) {
      return;
    }

    assistantLaunchHandledRef.current = true;
    handleOpenCreateProductModal();
  }, [
    handleOpenCreateProductModal,
    productionAssistantAction,
    productionAssistantMode,
  ]);

  useEffect(() => {
    if (
      !modalVisible ||
      editingProduct ||
      !productionAssistantMode ||
      productionAssistantAction !== "create-raw-material" ||
      assistantPrefillHandledRef.current
    ) {
      return;
    }

    const nextValues = {
      product_type: "Matières premières",
    };

    const assistantWarehouse = availableProductWarehouses.find(
      (warehouse) => warehouse.id === Number(assistantWarehouseId)
    );

    if (assistantWarehouse) {
      nextValues.warehouse = assistantWarehouse.id;
      setFormSelectedWarehouseCompanyId(assistantWarehouse.company_id || null);
    }

    form.setFieldsValue(nextValues);
    setActiveProductFormTab("1");
    assistantPrefillHandledRef.current = true;
  }, [
    assistantWarehouseId,
    availableProductWarehouses,
    editingProduct,
    form,
    modalVisible,
    productionAssistantAction,
    productionAssistantMode,
  ]);

  const fetchAllUnits = async () => {
    try {
      // Cet appel récupère toutes les unités, potentiellement sans filtre d'entreprise,
      // ou avec une logique pour récupérer celles pertinentes pour l'affichage général.
      // Pour l'instant, on suppose qu'il récupère toutes les unités nécessaires pour le tableau.
      const response = await axios.get(`${API_URL}/api/units`, {
        params: { limit: 1000 },
      }); // limite haute pour tout avoir
      if (response.data && response.data.units) {
        setAllUnits(response.data.units); // Met à jour allUnits
      }
    } catch (error) {
      if (!isCompanySuspendedError(error)) {
        message.error("Erreur lors du chargement de toutes les unités");
        console.error("[fetchAllUnits] Erreur :", error);
      }
    }
  };

  const fetchTaxes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/taxes`);
      if (response.data && response.data.taxes) {
        setTaxes(response.data.taxes);
      }
    } catch (error) {
      if (!isCompanySuspendedError(error)) {
        message.error("Erreur lors du chargement des taxes");
        console.error("[fetchTaxes] Erreur :", error);
      }
    }
  };

  const fetchProductionUsageProfiles = async (product) => {
    if (!product?.id) {
      setProductionUsageProfiles([]);
      return;
    }

    setLoadingProductionUsageProfiles(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/production/material-usage-profiles`,
        {
          params: {
            material_product_id: product.id,
            company_id: product.company_id || selectedCompanyId,
            warehouse_id: product.warehouse_id || selectedWarehouse || undefined,
            limit: 100,
          },
        }
      );

      setProductionUsageProfiles(
        normalizeProductionUsageProfiles(response.data?.profiles || [])
      );
    } catch (error) {
      console.error(
        "Erreur lors du chargement des usages en production:",
        error
      );
      setProductionUsageProfiles([]);
    } finally {
      setLoadingProductionUsageProfiles(false);
    }
  };

  const openQuickCategoryModal = () => {
    const quickCreationCompanyId =
      formSelectedWarehouseCompanyId || selectedCompanyId;

    if (!quickCreationCompanyId) {
      message.error("Veuillez d'abord sélectionner une entreprise.");
      return;
    }

    quickCategoryForm.resetFields();
    setQuickCategoryModalVisible(true);
  };

  const openQuickUnitModal = () => {
    if (!formSelectedWarehouseCompanyId) {
      message.error(
        "Veuillez d'abord sélectionner un établissement avant de créer une unité."
      );
      focusProductField("warehouse");
      return;
    }

    quickUnitForm.resetFields();
    setQuickUnitModalVisible(true);
  };

  const applyAssistantUnitPurchasePrice = () => {
    if (!assistantCanApplyUnitPurchasePrice) {
      message.warning(
        "Renseignez l'unite de stock, le contenu du conditionnement et son prix d'achat."
      );
      return;
    }

    form.setFieldsValue({
      prix_achat: Number(assistantDerivedUnitPurchasePrice.toFixed(4)),
    });

    message.success(
      `Prix d'achat unitaire calcule en ${assistantStockUnitShortName}.`
    );
  };

  const applyAssistantOpeningStock = () => {
    if (!assistantCanApplyOpeningStock) {
      message.warning(
        "Renseignez d'abord le contenu d'un conditionnement et le nombre de conditionnements en stock."
      );
      return;
    }

    form.setFieldsValue({
      quantite_stock: Number(assistantDerivedOpeningStock.toFixed(4)),
    });

    message.success(`Stock initial traduit en ${assistantStockUnitShortName}.`);
  };

  const applyAssistantAlertQuantity = () => {
    if (!assistantCanApplyAlertQuantity) {
      message.warning(
        "Renseignez d'abord le contenu d'un conditionnement et le seuil voulu en conditionnements."
      );
      return;
    }

    form.setFieldsValue({
      alerte_quantite: Number(assistantDerivedAlertQuantity.toFixed(4)),
    });

    message.success(`Seuil d'alerte traduit en ${assistantStockUnitShortName}.`);
  };

  const addPurchasePackagingProfile = (profile, options = {}) => {
    const { setAsDefault = false, successMessage = null } = options;

    setPurchasePackagingProfiles((currentProfiles) => {
      const nextProfiles = normalizePurchasePackagingProfilesState([
        ...currentProfiles,
        createPurchasePackagingProfileDraft({
          ...profile,
          is_default:
            setAsDefault || (!currentProfiles.length && profile?.is_default !== false),
        }),
      ]);

      if (successMessage) {
        message.success(successMessage);
      }

      return nextProfiles;
    });
  };

  const updatePurchasePackagingProfile = (index, field, value) => {
    setPurchasePackagingProfiles((currentProfiles) =>
      normalizePurchasePackagingProfilesState(
        currentProfiles.map((profile, profileIndex) =>
          profileIndex === index
            ? {
                ...profile,
                [field]:
                  field === "contained_quantity" ||
                  field === "reference_purchase_price"
                    ? normalizePositiveNumber(value)
                    : value,
              }
            : profile
        )
      )
    );
  };

  const removePurchasePackagingProfile = (index) => {
    setPurchasePackagingProfiles((currentProfiles) =>
      normalizePurchasePackagingProfilesState(
        currentProfiles.filter((_, profileIndex) => profileIndex !== index)
      )
    );
  };

  const setDefaultPurchasePackagingProfile = (index) => {
    setPurchasePackagingProfiles((currentProfiles) =>
      normalizePurchasePackagingProfilesState(
        currentProfiles.map((profile, profileIndex) => ({
          ...profile,
          is_default: profileIndex === index,
        }))
      )
    );
  };

  const loadPackagingProfileIntoAssistant = (index) => {
    const profile = purchasePackagingProfiles[index];

    if (!profile) {
      return;
    }

    const packagingQuantity = normalizePositiveNumber(
      profile.contained_quantity
    );
    const packagingPurchasePrice = normalizePositiveNumber(
      profile.reference_purchase_price
    );
    const unitPurchasePrice =
      packagingQuantity > 0 ? packagingPurchasePrice / packagingQuantity : 0;

    form.setFieldsValue({
      assistant_packaging_label: profile.label || "",
      assistant_packaging_quantity: packagingQuantity,
      assistant_packaging_purchase_price: packagingPurchasePrice,
    });

    if (packagingQuantity <= 0 || packagingPurchasePrice <= 0) {
      message.warning(
        "Conditionnement charge dans l'assistant. Renseignez son contenu et son prix d'achat pour appliquer un prix unitaire."
      );
      return;
    }

    form.setFieldsValue({
      prix_achat: Number(unitPurchasePrice.toFixed(4)),
    });

    message.success(
      `Conditionnement charge et prix d'achat applique: ${formatAssistantValue(
        unitPurchasePrice
      )} CFA / ${assistantStockUnitShortName}.`
    );
  };

  const saveAssistantPackagingProfile = (setAsDefault = false) => {
    if (!canSaveAssistantPackagingProfile) {
      message.warning(
        "Renseignez le libelle du conditionnement, le contenu et l'unite de stock avant de l'enregistrer."
      );
      return;
    }

    const nextProfile = {
      label: assistantConditioningName,
      contained_quantity: assistantPackagingQuantityValue,
      reference_purchase_price: assistantPackagingPurchasePriceValue,
      is_default: setAsDefault,
    };

    const existingIndex = purchasePackagingProfiles.findIndex(
      (profile) =>
        String(profile.label || "").trim().toLowerCase() ===
        assistantConditioningName.toLowerCase()
    );

    if (existingIndex >= 0) {
      setPurchasePackagingProfiles((currentProfiles) =>
        normalizePurchasePackagingProfilesState(
          currentProfiles.map((profile, profileIndex) =>
            profileIndex === existingIndex
              ? {
                  ...profile,
                  ...nextProfile,
                  is_default: setAsDefault || profile.is_default,
                }
              : setAsDefault
              ? {
                  ...profile,
                  is_default: false,
                }
              : profile
          )
        )
      );

      message.success(
        setAsDefault
          ? "Profil de conditionnement par defaut mis a jour."
          : "Profil de conditionnement mis a jour."
      );
      return;
    }

    addPurchasePackagingProfile(nextProfile, {
      setAsDefault,
      successMessage: setAsDefault
        ? "Conditionnement ajoute comme profil par defaut."
        : "Conditionnement ajoute aux profils durables.",
    });
  };

  const addEmptyPurchasePackagingProfile = () => {
    addPurchasePackagingProfile(
      {
        label: "",
        contained_quantity: 0,
        reference_purchase_price: 0,
      },
      {
        setAsDefault: purchasePackagingProfiles.length === 0,
      }
    );
  };

  const handleQuickCategorySubmit = async (values) => {
    if (subscriptionWriteRestricted || !hasPermission("Gestion Commerciale.Produits.Categories.create")) return;
    const mutationContext = productContextKey;
    const quickCreationCompanyId =
      formSelectedWarehouseCompanyId || selectedCompanyId;
    const normalizedName = String(values.name || "").trim();
    const normalizedDescription = String(values.description || "").trim();

    if (!quickCreationCompanyId) {
      message.error("Aucune entreprise disponible pour créer la catégorie.");
      return;
    }

    setQuickCategoryLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/categories`, {
        company_id: quickCreationCompanyId,
        name: normalizedName,
        description: normalizedDescription,
        slug: generateSlug(normalizedName),
      });

      const createdCategory = response.data;
      void invalidateCategories();
      if (productContextRef.current.key !== mutationContext || !productContextRef.current.open) return;

      setCategories((prev) =>
        [...prev.filter((category) => category.id !== createdCategory.id), createdCategory].sort(
          (left, right) =>
            String(left.name || "").localeCompare(String(right.name || ""), "fr", {
              sensitivity: "base",
            })
        )
      );

      form.setFieldsValue({ id_categorie: createdCategory.id });
      quickCategoryForm.resetFields();
      setQuickCategoryModalVisible(false);
      message.success(`Catégorie "${createdCategory.name}" créée avec succès`);
    } catch (error) {
      console.error("[handleQuickCategorySubmit] Erreur :", error);
      message.error(
        error.response?.data?.error ||
          "Erreur lors de la création rapide de la catégorie"
      );
    } finally {
      setQuickCategoryLoading(false);
    }
  };

  const handleQuickUnitSubmit = async (values) => {
    if (subscriptionWriteRestricted || !hasPermission("Gestion Commerciale.Produits.Unites.create")) return;
    const mutationContext = productContextKey;
    const normalizedName = String(values.name || "").trim();
    const normalizedShortName = String(values.short_name || "").trim();
    const normalizedBaseUnit = String(values.base_unit || "").trim();

    if (!formSelectedWarehouseCompanyId) {
      message.error("Aucune entreprise disponible pour créer l'unité.");
      return;
    }

    setQuickUnitLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/units`, {
        company_id: formSelectedWarehouseCompanyId,
        name: normalizedName,
        short_name: normalizedShortName,
        base_unit: normalizedBaseUnit || null,
        operator: "*",
        operator_value: 1,
        is_deletable: true,
      });

      const createdUnitId = response.data?.id;
      void invalidateUnits();
      const nextUnits = (await productFormOptions.refresh({ force: true }).catch(() => null))?.units || [];
      if (productContextRef.current.key !== mutationContext || !productContextRef.current.open) return;
      let createdUnit =
        nextUnits.find((unit) => unit.id === createdUnitId) ||
        nextUnits.find(
          (unit) =>
            unit.name === normalizedName && unit.short_name === normalizedShortName
        );

      if (!createdUnit && createdUnitId) {
        createdUnit = {
          id: createdUnitId,
          company_id: formSelectedWarehouseCompanyId,
          name: normalizedName,
          short_name: normalizedShortName,
          base_unit: normalizedBaseUnit || null,
        };
        setFormUnits((prev) => [...prev, createdUnit]);
      }

      if (createdUnit) {
        setAllUnits((prev) =>
          [...prev.filter((unit) => unit.id !== createdUnit.id), createdUnit].sort(
            (left, right) =>
              String(left.name || "").localeCompare(String(right.name || ""), "fr", {
                sensitivity: "base",
              })
          )
        );
        form.setFieldsValue({ id_unit: createdUnit.id });
      }

      quickUnitForm.resetFields();
      setQuickUnitModalVisible(false);
      message.success(`Unité "${normalizedName}" créée avec succès`);
    } catch (error) {
      console.error("[handleQuickUnitSubmit] Erreur :", error);
      message.error(
        error.response?.data?.error ||
          "Erreur lors de la création rapide de l'unité"
      );
    } finally {
      setQuickUnitLoading(false);
    }
  };

  // --- Récupération des produits avec filtrage côté serveur ---
  const fetchProducts = async () => {
    try {
      setLoading(true);

      // Protection supplémentaire : ne pas faire d'appel si pas d'entreprise ou de magasin
      if (!selectedCompany || !selectedWarehouse) {
        setProducts([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        return;
      }

      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        // Note: Les produits de type "production" sont affichés mais non modifiables
        // La modification est bloquée côté backend (403 PRODUCTION_TYPE_PROTECTED)
      };
      // Variantes (profil e-commerce) : regroupe les declinaisons sous leur parent
      // (masque les enfants, agrege le stock). Sans effet si aucun produit a variantes.
      if (isVariantsEligible) {
        params.include_variants_grouped = "true";
      }
      const response = await axios.get(`${API_URL}/api/produits`, { params });
      if (response.data && response.data.products) {
        const transformedProducts = response.data.products.map((product) => ({
          ...product,
          key: product.id,
          warehouse_nom: product.warehouse_nom || "N/A",
        }));

        // On fait confiance au filtrage côté serveur uniquement

        setProducts(transformedProducts);
        setPagination((prev) => ({ ...prev, total: response.data.total || 0 }));
      }
    } catch (error) {
      if (!isCompanySuspendedError(error)) {
        message.error("Erreur lors du chargement des produits");
        console.error("[fetchProducts] Erreur :", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Charge les declinaisons (enfants) d'un produit parent a l'expansion de sa ligne.
  const loadVariantChildren = async (parentId) => {
    if (variantChildrenMap[parentId]) return; // deja charge
    setLoadingVariantChildren((prev) => ({ ...prev, [parentId]: true }));
    try {
      const response = await axios.get(
        `${API_URL}/api/produits/${parentId}/variants`
      );
      setVariantChildrenMap((prev) => ({
        ...prev,
        [parentId]: response.data?.variants || [],
      }));
    } catch (error) {
      console.error("[loadVariantChildren] Erreur :", error);
      message.error("Erreur lors du chargement des déclinaisons");
    } finally {
      setLoadingVariantChildren((prev) => ({ ...prev, [parentId]: false }));
    }
  };

  // --- Fonction pour vérifier si un produit existe déjà (validation UX) ---
  const checkProductExists = async (field, value, warehouseId, excludeId = null) => {
    if (!value) return null;

    // Pour le nom, warehouse_id est requis
    if (field === 'name' && !warehouseId) {
      return null;
    }

    try {
      const params = {
        field,
        value,
      };

      if (field === 'name') {
        params.warehouse_id = warehouseId;
      }

      if (excludeId) {
        params.exclude_id = excludeId;
      }

      const response = await axios.get(`${API_URL}/api/produits/check-exists`, {
        params
      });

      return response.data.exists ? response.data.message : null;
    } catch (error) {
      console.error('[checkProductExists] Erreur:', error);
      return null; // En cas d'erreur réseau, ne pas bloquer l'utilisateur
    }
  };

  // --- Function to handle CSV Upload ---
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error("Veuillez sélectionner un fichier CSV.");
      return;
    }

    const formData = new FormData();
    // Use actual File object; originFileObj may be undefined when using Dragger
    const fileObj = fileList[0].originFileObj || fileList[0];
    formData.append("importFile", fileObj);

    setIsUploading(true);
    setImportResult(null);

    try {
      // Let axios set Content-Type with appropriate boundary
      const response = await axios.post(
        `${API_URL}/api/produits/import`,
        formData
      );
      setImportResult(response.data);
      if (response.data?.successCount > 0) void invalidateProducts();
      message.success(response.data.message);

      // Vérifier si l'importation a réussi (au moins un succès)
      if (response.data && response.data.successCount > 0) {
        // Laisser un petit délai pour que l'utilisateur voie le message de succès global
        setTimeout(() => {
          setImportModalVisible(false); // Fermer le modal
          setFileList([]); // Réinitialiser la liste des fichiers
          setImportResult(null); // Réinitialiser les résultats de l'import précédent
        }, 2000); // Délai de 2 secondes
      } else if (response.data && response.data.errorCount > 0) {
        // Si que des erreurs, ne pas fermer, laisser l'utilisateur voir les erreurs
        // setFileList([]); // On peut aussi vider le fichier ici si on veut forcer une nouvelle sélection
      }
    } catch (error) {
      console.error("Erreur lors de l'importation:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.details ||
        "Erreur inconnue lors de l'importation.";
      const errorDetails = error.response?.data?.errors;
      setImportResult({
        errorCount: errorDetails ? errorDetails.length : 1,
        errors: errorDetails || [{ error: errorMessage }],
      });
      message.error(`Échec de l'importation: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  // --- Function to create and trigger download of CSV template ---
  const downloadTemplate = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    const headers = [
      "item_code",
      "barcode_value",
      "name",
      "warehouse_name",
      "category_name",
      "unit_short_name",
      "product_type",
      "barcode_symbology",
      "purchase_price",
      "sales_price",
      "stock_quantitiy_alert",
      "tax_name",
      "slug",
      "description",
      "opening_stock",
    ];
    const exampleRow = [
      "PROD001",
      "1234567890123",
      "Exemple Produit 1",
      "Entrepôt Principal",
      "Électronique",
      "pcs",
      "single",
      "CODE128",
      "15000",
      "25000",
      "10",
      "TVA 18%",
      "exemple-produit-1",
      "Ceci est une description exemple",
      "50",
    ];
    const exampleRow2 = [
      "MATPREM01",
      "MAT-A-2026",
      "Matière A",
      "Entrepôt Matières",
      "Matières Premières",
      "kg",
      "raw",
      "CODE128",
      "5000",
      "0",
      "50",
      "",
      "",
      "Composant essentiel",
      "250.5",
    ];

    const formatCsvRow = (row) =>
      row.map((value) => `"${value.replace(/"/g, '""')}"`).join(",");

    const csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      formatCsvRow(exampleRow) +
      "\n" +
      formatCsvRow(exampleRow2);

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "template_import_produits.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success("Téléchargement du template lancé.");
  };

  // --- MODIFIED: Function to handle product export (CSV or Excel) ---
  const handleExportProducts = async (format = "csv") => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    setExporting(true);
    try {
      const apiParams = {
        ...filters,
        all: true,
      };

      const response = await axios.get(`${API_URL}/api/produits`, {
        params: apiParams,
      });
      const productsToExport = response.data.products;

      if (!productsToExport || productsToExport.length === 0) {
        message.info("Aucun produit à exporter selon les filtres actuels.");
        setExporting(false);
        return;
      }

      const exportData = productsToExport.map((product) => {
        const unit = allUnits.find((u) => u.id === product.unit_id);
        return {
          "Code Article": product.item_code || "",
          "Code-barres": product.barcode_value || product.item_code || "",
          "Nom du Produit": product.name || "",
          "Établissement principal": product.warehouse_nom || "",
          Catégorie: product.categorie_nom || "",
          "Unité (Abrégé)": unit ? unit.short_name : "",
          "Type de Produit": product.product_type || "",
          "Symbole Code-barres": product.barcode_symbology || "",
          "Prix d'Achat":
            product.purchase_price !== null &&
            product.purchase_price !== undefined
              ? Number(product.purchase_price)
              : "",
          "Prix de Vente":
            product.sales_price !== null && product.sales_price !== undefined
              ? Number(product.sales_price)
              : "",
          "Stock Actuel":
            product.current_stock !== null &&
            product.current_stock !== undefined
              ? Number(product.current_stock)
              : "",
          "Alerte Quantité":
            product.stock_quantitiy_alert !== null &&
            product.stock_quantitiy_alert !== undefined
              ? Number(product.stock_quantitiy_alert)
              : "",
          Slug: product.slug || "",
          Description: product.description || "",
        };
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

      if (format === "csv") {
        const csvHeaders = Object.keys(exportData[0]);
        const csvRows = exportData.map((row) =>
          csvHeaders
            .map((header) => `"${String(row[header]).replace(/"/g, '""')}"`)
            .join(",")
        );
        const csvContent =
          "data:text/csv;charset=utf-8," +
          csvHeaders.join(",") +
          "\n" +
          csvRows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `export_produits_${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (format === "excel") {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Produits");
        XLSX.writeFile(workbook, `export_produits_${timestamp}.xlsx`);
      }

      message.success(
        `Produits exportés en ${format.toUpperCase()} avec succès.`
      );
    } catch (error) {
      console.error(
        `[handleExportProducts ${format.toUpperCase()}] Erreur :`,
        error
      );
      message.error(
        `Erreur lors de l'exportation des produits en ${format.toUpperCase()}.`
      );
    } finally {
      setExporting(false);
    }
  };

  // Menu for the export dropdown
  const exportMenu = (
    <Menu onClick={(e) => handleExportProducts(e.key)}>
      <Menu.Item key="csv" icon={<DownloadOutlined />}>
        Exporter en CSV
      </Menu.Item>
      <Menu.Item key="excel" icon={<DownloadOutlined />}>
        Exporter en Excel
      </Menu.Item>
    </Menu>
  );

  // --- Définition des colonnes du tableau ---
  const columns = [
    {
      title: "Produit",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true, ignorePunctuation: true }),
      render: (text, record) => {
        const { current_stock, stock_quantitiy_alert, warehouse_nom, main_warehouse_nom } = record;
        let dotColor = "green";
        if (current_stock <= 0) {
          dotColor = "red";
        } else if (current_stock <= stock_quantitiy_alert) {
          dotColor = "orange";
        } else {
          dotColor = "green";
        }

        // ✅ Détecter si le produit est transféré
        const isTransferred = main_warehouse_nom &&
                             main_warehouse_nom !== warehouse_nom &&
                             warehouse_nom !== "N/A";

        // 🔒 Détecter si le produit est de type "production"
        const isProductionType = record.product_type === "production";

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dotColor,
              }}
            ></span>
            <span>{text}</span>
            <ProductStatusBadge product={record} size="small" />
            {record.is_variant_parent && (
              <Tooltip title="Produit à variantes — cliquez sur la flèche pour voir les déclinaisons">
                <Tag color="geekblue" style={{ fontSize: "11px", marginLeft: "4px" }}>
                  {Number(record.variants_count) > 0
                    ? `${record.variants_count} variantes`
                    : "Variantes"}
                </Tag>
              </Tooltip>
            )}
            {isTransferred && (
              <Tooltip title={`Produit transféré depuis ${main_warehouse_nom}`}>
                <Tag color="blue" style={{ fontSize: '11px', marginLeft: '4px' }}>
                  Transféré
                </Tag>
              </Tooltip>
            )}
            {isProductionType && (
              <Tooltip
                title={`${productionTerminology.unitSingularTitle} - Modification uniquement depuis l'interface dédiée`}
              >
                <Tag color="purple" style={{ fontSize: '11px', marginLeft: '4px' }}>
                  {productionTerminology.unitSingularTitle}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Unité",
      dataIndex: "unit_id",
      key: "unit",
      render: (unit_id) => {
        const unit = allUnits.find((u) => Number(u.id) === Number(unit_id));
        return unit ? `${unit.name} (${unit.short_name})` : "N/A";
      },
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_nom",
      key: "warehouse",
      // 🔒 SÉCURITÉ: Filtrer uniquement les warehouses assignés à l'utilisateur
      filters: (user?.assigned_warehouses || [])
        .filter((w) => !selectedCompany || w.company_id === selectedCompany)
        .map((w) => ({
          text: w.name,
          value: w.id,
        })),
      render: (text, record) => {
        const currentContextWarehouseName = text || "N/A";
        const mainWarehouseName = record.main_warehouse_nom;

        if (
          mainWarehouseName &&
          mainWarehouseName !== currentContextWarehouseName &&
          currentContextWarehouseName !== "N/A"
        ) {
          return (
            <Tooltip title={`Établissement principal: ${mainWarehouseName}`}>
              {currentContextWarehouseName}{" "}
              <InfoCircleOutlined
                style={{ color: "var(--color-primary)", marginLeft: "4px" }}
              />
            </Tooltip>
          );
        }
        return currentContextWarehouseName;
      },
    },
    {
      title: "Catégorie",
      dataIndex: "categorie_nom",
      key: "categorie",
      render: (text) => text || "N/A",
    },
    {
      title: "Stock actuel",
      dataIndex: "current_stock",
      key: "current_stock",
      sorter: (a, b) => a.current_stock - b.current_stock,
      render: (value) => formatNumber(value),
    },
    {
      title: "Prix d'achat",
      dataIndex: "purchase_price",
      key: "purchase_price",
      render: (value) => `${formatNumber(value)} CFA`,
      sorter: (a, b) => a.purchase_price - b.purchase_price,
    },
    {
      title: "Prix de vente",
      dataIndex: "sales_price",
      key: "sales_price",
      render: (value) => `${formatNumber(value)} CFA`,
      sorter: (a, b) => a.sales_price - b.sales_price,
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "center",
      render: (text, record) => {
        const canView = hasPermission(
          "Gestion Commerciale.Produits.Produits.view"
        );
        const canEdit = hasPermission(
          "Gestion Commerciale.Produits.Produits.edit"
        );
        const canDelete = hasPermission(
          "Gestion Commerciale.Produits.Produits.delete"
        );
        const canArchive = hasPermission(
          "Gestion Commerciale.Produits.Produits.archive"
        );
        const canDisable = hasPermission(
          "Gestion Commerciale.Produits.Produits.disable"
        );
        const canRestore = hasPermission(
          "Gestion Commerciale.Produits.Produits.restore"
        );
        const canForceDelete = hasPermission(
          "Gestion Commerciale.Produits.Produits.force_delete"
        );

        // ✅ NOUVELLE LOGIQUE: Détecter si le produit est transféré (pas dans son magasin propriétaire)
        const currentWarehouseName = record.warehouse_nom;
        const mainWarehouseName = record.main_warehouse_nom;
        const isTransferredProduct = mainWarehouseName &&
                                      mainWarehouseName !== currentWarehouseName &&
                                      currentWarehouseName !== "N/A";

        // 🔒 PROTECTION: Détecter si le produit est de type "production"
        const isProductionType = record.product_type === "production";
        // 🔒 PROTECTION: Produit PARENT à variantes (modification via interface dédiée)
        const isVariantParent = Boolean(record.is_variant_parent);

        // If no actions are possible for this row, render null or an empty fragment
        if (!canView && !canEdit && !canDelete && !canArchive && !canDisable && !canRestore && !canForceDelete) {
          return null;
        }

        const isDeleted = record.is_deleted === 1;
        const isArchived = record.status === 'archived';
        const isDisabled = record.status === 'disabled';
        const isActive = record.status === 'actif' && !isDeleted;

        const actionItems = [];
        const ownershipBlocked = isTransferredProduct || isProductionType;

        if (canView) {
          actionItems.push({
            key: "view",
            icon: <EyeOutlined />,
            label: "Voir le détail",
            onClick: () => handleView(record),
          });
        }

        if (canEdit && !isDeleted) {
          const editDisabled =
            subscriptionWriteRestricted || ownershipBlocked || isVariantParent;
          const editLabel = subscriptionWriteRestricted
            ? "Modifier — souscription en lecture seule"
            : isProductionType
            ? "Modification via l'interface Production"
            : isVariantParent
            ? "Modification via les déclinaisons"
            : isTransferredProduct
            ? `Produit rattaché à ${mainWarehouseName}`
            : "Modifier";
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: editLabel,
            disabled: editDisabled,
            onClick: () => handleEdit(record),
          });
        }

        if (isVariantParent && canEdit && !isDeleted) {
          actionItems.push({
            key: "variants",
            icon: <AppstoreOutlined />,
            label: subscriptionWriteRestricted
              ? "Déclinaisons — souscription en lecture seule"
              : "Gérer les déclinaisons",
            disabled: subscriptionWriteRestricted,
            onClick: () => setManageVariantsParentId(record.id),
          });
        }

        const statusItems = [];

        if (isDeleted && canRestore) {
          statusItems.push({
            key: "restore",
            icon: <UndoOutlined />,
            label: isProductionType
              ? "Restauration via l'interface Production"
              : "Restaurer",
            disabled: subscriptionWriteRestricted || isProductionType,
            onClick: () => handleRestore(record),
          });
        }

        if (isActive && canArchive) {
          statusItems.push({
            key: "archive",
            icon: <InboxOutlined />,
            label: ownershipBlocked ? "Archivage indisponible" : "Archiver",
            disabled: subscriptionWriteRestricted || ownershipBlocked,
            onClick: () => handleArchive(record),
          });
        }

        if (isActive && canDisable) {
          statusItems.push({
            key: "disable",
            icon: <StopOutlined />,
            label: ownershipBlocked
              ? "Désactivation indisponible"
              : "Désactiver",
            disabled: subscriptionWriteRestricted || ownershipBlocked,
            onClick: () => handleDisable(record),
          });
        }

        if ((isArchived || isDisabled) && canRestore) {
          statusItems.push({
            key: "reactivate",
            icon: <UndoOutlined />,
            label: ownershipBlocked
              ? "Réactivation indisponible"
              : "Réactiver",
            disabled: subscriptionWriteRestricted || ownershipBlocked,
            onClick: () => handleRestore(record),
          });
        }

        if (statusItems.length > 0) {
          if (actionItems.length > 0) actionItems.push({ type: "divider" });
          actionItems.push(...statusItems);
        }

        if (canDelete && !isDeleted) {
          if (actionItems.length > 0) actionItems.push({ type: "divider" });
          actionItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: subscriptionWriteRestricted
              ? "Supprimer — souscription en lecture seule"
              : ownershipBlocked
                ? "Suppression indisponible"
                : "Supprimer",
            disabled: subscriptionWriteRestricted || ownershipBlocked,
            danger: !subscriptionWriteRestricted && !ownershipBlocked,
            onClick: () => handleDelete(record),
          });
        }

        return (
          <RowActionsMenu
            items={actionItems}
            ariaLabel={`Afficher les actions du produit ${record.name || record.id}`}
          />
        );
      },
    },
  ];

  const productTypeColumn = {
    title: "Type de produit",
    dataIndex: "product_type",
    key: "product_type",
    render: (value) => {
      const typeMeta = getProductTypeMeta(value);
      return <Tag color={typeMeta.color}>{typeMeta.label}</Tag>;
    },
  };

  const orderedColumns = [
    columns.find((column) => column.key === "name"),
    columns.find((column) => column.key === "categorie"),
    productTypeColumn,
    columns.find((column) => column.key === "current_stock"),
    columns.find((column) => column.key === "unit"),
    columns.find((column) => column.key === "purchase_price"),
    columns.find((column) => column.key === "sales_price"),
    columns.find((column) => column.key === "actions"),
  ].filter(Boolean);

  const canViewProductDetails = hasPermission(
    "Gestion Commerciale.Produits.Produits.view"
  );

  // La fiche reste attachée au contexte dans lequel elle a été ouverte.
  const detailsContext = JSON.stringify([user?.id, selectedCompanyId, selectedWarehouse]);
  useEffect(() => { setDetailsTarget(null); }, [detailsContext]);
  const handleView = (record) => {
    if (!canViewProductDetails) return;
    if (!selectedCompanyId || !selectedWarehouse) {
      message.warning("Sélectionnez une entreprise et un établissement pour consulter le produit.");
      return;
    }
    setDetailsTarget({ id: record.id, context: detailsContext });
  };

  // --- Préchargement du formulaire en modification ---
  const handleEdit = async (tableRecord) => {
    const requestId = ++productEditRequestRef.current;
    const editContext = productContextKey;
    try {
      setLoading(true); // Indicate loading while fetching product details

      // 1. Fetch full, definitive product data for editing
      const response = await axios.get(
        `${API_URL}/api/produits/${tableRecord.id}`,
        {
          params: selectedCompanyId
            ? { company_id: selectedCompanyId }
            : undefined,
        }
      );
      const productToEdit = response.data;
      if (requestId !== productEditRequestRef.current || editContext !== productContextRef.current.key) return;

      setEditingProduct(productToEdit); // Use the more complete data

      // 2. Ensure the warehouses list is available (the existing fetchWarehouses call tied to selectedCompany is generally okay)
      // If the product's main warehouse's company differs from selectedCompany, `warehouses` might not contain it.
      // However, productToEdit.warehouse_id will be used directly.
      if (selectedCompany) {
        // This ensures warehouses for the current global context are loaded.
        // The logic below will try to find the product's warehouse in this list.
        await fetchWarehouses();
      }
      if (requestId !== productEditRequestRef.current || editContext !== productContextRef.current.key) return;

      // 3. Construct formValues from the fetched definitive product data
      const formValues = {
        nom_produit: productToEdit.name,
        warehouse: productToEdit.warehouse_id, // Main warehouse_id of the product
        product_type:
          productToEdit.product_type === "single"
            ? "Produits finis"
            : "Matières premières",
        stock_management_mode: productToEdit.stock_management_mode || "standard",
        image: productToEdit.image
          ? [
              {
                uid: "-1",
                name: productToEdit.image,
                status: "done",
                url: getProductImageUrl(productToEdit.image),
              },
            ]
          : [],
        prix_achat: Number(productToEdit.purchase_price), // Specific to main warehouse
        prix_vente: Number(productToEdit.sales_price), // Specific to main warehouse
        quantite_stock: Number(productToEdit.current_stock), // Current stock in main warehouse
        alerte_quantite: Number(productToEdit.stock_quantitiy_alert), // Specific to main warehouse
        barcode_symbology: productToEdit.barcode_symbology,
        item_code: productToEdit.item_code,
        barcode_value: productToEdit.barcode_value || productToEdit.item_code,
        slug: productToEdit.slug,
        id_categorie: productToEdit.category_id,
        id_unit: productToEdit.unit_id,
        taxe: productToEdit.tax_id, // Specific to main warehouse
        description: productToEdit.description,
      };

      const barcodeValue =
        productToEdit.barcode_value || productToEdit.item_code || null;
      if (barcodeValue) {
        setBarcodeGenerated(true);
        setCurrentBarcode(barcodeValue);
      } else {
        setBarcodeGenerated(false);
        setCurrentBarcode(null);
      }

      form.setFieldsValue(formValues);
      setPurchasePackagingProfiles(
        normalizePurchasePackagingProfilesState(
          productToEdit.purchase_packaging_profiles || []
        )
      );
      if (productToEdit.product_type === "raw") {
        fetchProductionUsageProfiles(productToEdit);
      } else {
        setProductionUsageProfiles([]);
      }

      // 4. Déterminer company_id de l'entrepôt du produit en cours d'édition pour charger les bonnes unités
      if (productToEdit.warehouse_id) {
        // Try to find the warehouse in the currently loaded `warehouses` state
        const productWarehouseInfo = warehouses.find(
          (w) => w.id === productToEdit.warehouse_id
        );
        if (productWarehouseInfo && productWarehouseInfo.company_id) {
          setFormSelectedWarehouseCompanyId(productWarehouseInfo.company_id);
        } else {
          // Fallback: If warehouse not in list (e.g. different global company selected)
          // or warehouse has no company_id, use the product's own company_id.
          console.warn(
            `[handleEdit] L'établissement principal du produit (ID: ${productToEdit.warehouse_id}) n'a pas été trouvé dans la liste des établissements actuellement chargés pour la company sélectionnée, ou il manque company_id. Tentative d'utilisation de productToEdit.company_id (${productToEdit.company_id}) pour les unités.`
          );
          if (productToEdit.company_id) {
            setFormSelectedWarehouseCompanyId(productToEdit.company_id);
          } else {
            console.warn(
              `[handleEdit] productToEdit.company_id est également manquant. Les unités pourraient ne pas se charger.`
            );
            setFormSelectedWarehouseCompanyId(null);
          }
        }
      } else {
        // No main warehouse associated with the product at the 'products' table level
        setFormSelectedWarehouseCompanyId(null);
      }

      setActiveProductFormTab("1");
      setModalVisible(true);
    } catch (error) {
      message.error(
        error.response?.data?.error ||
          "Erreur lors du chargement des données du produit pour modification."
      );
      console.error("[handleEdit] Erreur :", error.response || error);
    } finally {
      setLoading(false);
    }
  };

  // --- Suppression sécurisée d'un produit ---
  const handleDelete = (record) => {
    setSelectedProductForAction(record);
    setDeleteModalVisible(true);
  };

  const handleSecureAction = async (action, reason, force = false) => {
    if (!selectedProductForAction) return;

    // Les suppressions sont gérées par DeleteConfirmationModal
    if (action === 'delete') {
      return;
    }

    setActionLoading(true);

    // *** AMÉLIORATION UX : Messages de progression spécifiques ***
    const progressMessages = {
      archive: 'Archivage du produit en cours...',
      disable: 'Désactivation du produit en cours...',
      restore: 'Restauration du produit en cours...'
    };

    message.loading({
      content: progressMessages[action] || 'Traitement en cours...',
      key: 'productAction',
      duration: 0 // Garder jusqu'à completion
    });

    try {
      const startTime = Date.now();

      const endpoint = `${API_URL}/api/produits/${selectedProductForAction.id}/${action}`;
      const method = action === 'restore' ? 'patch' : 'patch';
      const payload = { action, reason };

      console.log(`[PRODUCT_ACTION] Début ${action} pour produit ${selectedProductForAction.id}`);

      await axios[method](endpoint, payload);
      void invalidateProducts();

      const duration = Date.now() - startTime;
      console.log(`[PRODUCT_ACTION] ${action} terminé en ${duration}ms`);

      const actionMessages = {
        archive: 'Produit archivé avec succès',
        disable: 'Produit désactivé avec succès',
        restore: 'Produit restauré avec succès'
      };

      message.success({
        content: `${actionMessages[action] || 'Action effectuée avec succès'} (${duration}ms)`,
        key: 'productAction',
        duration: 3
      });

      // Pour les actions non-suppression, recharger la liste
      fetchProducts();
      setSelectedProductForAction(null);

    } catch (error) {
      console.error(`Erreur lors de l'action ${action}:`, error);

      // *** AMÉLIORATION UX : Messages d'erreur plus informatifs ***
      const errorMessage = error.response?.data?.error ||
                         error.response?.data?.message ||
                         error.message ||
                         `Erreur lors de ${action}`;

      message.error({
        content: errorMessage,
        key: 'productAction',
        duration: 6
      });

      if (error.response?.status === 409) {
        // Ne pas fermer la modal en cas de conflit - elle gère l'affichage des dépendances
        return;
      }

      message.error(error.response?.data?.error || `Erreur lors de l'action ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Actions individuelles pour les boutons
  const handleArchive = (record) => {
    setSelectedProductForAction(record);
    Modal.confirm({
      title: 'Archiver le produit',
      content: 'Voulez-vous archiver ce produit ? Il restera accessible mais ne sera plus actif.',
      okText: 'Archiver',
      cancelText: 'Annuler',
      onOk: () => handleSecureAction('archive', 'Archivage via interface utilisateur')
    });
  };

  const handleDisable = (record) => {
    setSelectedProductForAction(record);
    Modal.confirm({
      title: 'Désactiver le produit',
      content: 'Voulez-vous désactiver ce produit ? Il ne sera plus disponible à la vente.',
      okText: 'Désactiver',
      cancelText: 'Annuler',
      onOk: () => handleSecureAction('disable', 'Désactivation via interface utilisateur')
    });
  };

  const handleRestore = (record) => {
    setSelectedProductForAction(record);
    Modal.confirm({
      title: 'Restaurer le produit',
      content: 'Voulez-vous restaurer ce produit et le rendre à nouveau actif ?',
      okText: 'Restaurer',
      cancelText: 'Annuler',
      onOk: () => handleSecureAction('restore', 'Restauration via interface utilisateur')
    });
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value || 'all' }));
  };

  // --- Création ou mise à jour du produit ---
  // Soumission specifique au mode VARIANTES : cree un produit parent + N declinaisons
  // via l'endpoint dedie (POST /api/produits/with-variants). Reutilise les champs
  // partages du formulaire standard (entrepot, nom, unite, categorie, taxe, image...).
  const handleSubmitVariants = async (values) => {
    if (!variantRows.length) {
      message.error(
        "Aucune déclinaison. Générez les combinaisons dans l'onglet « Déclinaisons »."
      );
      setActiveProductFormTab("variants");
      return;
    }
    const missingPrice = variantRows.find(
      (v) =>
        v.sales_price === null ||
        v.sales_price === undefined ||
        v.sales_price === ""
    );
    if (missingPrice) {
      message.error(
        `Prix de vente manquant pour la déclinaison « ${missingPrice.label} ».`
      );
      setActiveProductFormTab("variants");
      return;
    }

    const warehouseId = Number(values.warehouse);
    const selectedWarehouseLocal = warehouses.find((w) => w.id === warehouseId);
    if (!selectedWarehouseLocal) {
      form.setFields([{ name: "warehouse", errors: ["Établissement non trouvé"] }]);
      focusProductField("warehouse");
      message.error("Établissement non trouvé");
      return;
    }

    const fd = new FormData();
    fd.append("warehouse_id", warehouseId);
    if (selectedWarehouseLocal.company_id) {
      fd.append("company_id", selectedWarehouseLocal.company_id);
    }
    fd.append("name", values.nom_produit);
    fd.append("unit_id", values.id_unit ?? "");
    fd.append("barcode_symbology", values.barcode_symbology || "CODE128");
    fd.append(
      "stock_management_mode",
      values.stock_management_mode || "standard"
    );
    if (values.id_categorie) fd.append("category_id", values.id_categorie);
    if (values.description) fd.append("description", values.description);
    if (values.taxe) fd.append("tax_id", values.taxe);
    // Code parent (base) : laisse le backend generer "VP-<nom>" si vide.
    if (values.item_code) fd.append("item_code", values.item_code);

    fd.append(
      "variant_attributes",
      JSON.stringify(buildVariantAttributes(variantAxes))
    );

    const variantsPayload = variantRows.map((v) => ({
      variant_values: v.variant_values,
      label: v.label,
      item_code: v.item_code ? String(v.item_code).trim() : undefined,
      barcode_value: v.barcode_value
        ? String(v.barcode_value).trim()
        : undefined,
      purchase_price: v.purchase_price ?? 0,
      sales_price: v.sales_price ?? 0,
      tax_id: v.tax_id ?? values.taxe ?? undefined,
      opening_stock: v.opening_stock ?? 0,
      stock_quantitiy_alert: v.stock_quantitiy_alert ?? 0,
    }));
    fd.append("variants", JSON.stringify(variantsPayload));

    // Image principale (partagee) : reutilise le champ image du formulaire standard.
    if (values.image && values.image[0] && values.image[0].originFileObj) {
      fd.append("image", values.image[0].originFileObj);
    }
    variantRows.forEach((v, i) => {
      if (v.imageFile) fd.append(`variant_image_${i}`, v.imageFile);
    });

    try {
      const response = await axios.post(
        `${API_URL}/api/produits/with-variants`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      void invalidateProducts();
      message.success(
        `Produit à variantes créé (${
          response.data?.variants?.length || variantRows.length
        } déclinaison(s)).`
      );
      handleModalClose();
      fetchProducts();
    } catch (error) {
      console.error("[handleSubmitVariants] Erreur:", error);
      const data = error.response?.data;
      if (data?.code === "VARIANTS_PROFILE_NOT_ELIGIBLE") {
        message.error(
          "Les produits à variantes sont réservés aux entreprises au profil e-commerce."
        );
      } else if (data?.field) {
        message.error(data.error);
      } else {
        message.error(
          data?.details ||
            data?.error ||
            "Erreur lors de la création du produit à variantes"
        );
      }
    }
  };

  // Conversion d'un produit simple existant. Le backend cree un nouveau parent
  // et reutilise l'ID du produit courant pour la combinaison choisie afin de ne
  // perdre ni stock, ni mouvement, ni vente/achat historique.
  const handleConvertToVariants = async (values) => {
    if (variantRows.length < 2) {
      message.error(
        "La conversion nécessite au moins deux déclinaisons. Définissez les axes puis générez les combinaisons."
      );
      setActiveProductFormTab("variants");
      return;
    }

    const existingVariant = variantRows.find(
      (variant) => variant.key === existingVariantKey
    );
    if (!existingVariant) {
      message.error(
        "Choisissez la déclinaison qui correspond au produit actuel."
      );
      setActiveProductFormTab("variants");
      return;
    }

    const missingPrice = variantRows.find(
      (variant) =>
        variant.sales_price === null ||
        variant.sales_price === undefined ||
        variant.sales_price === ""
    );
    if (missingPrice) {
      message.error(
        `Prix de vente manquant pour la déclinaison « ${missingPrice.label} ».`
      );
      setActiveProductFormTab("variants");
      return;
    }

    const fd = new FormData();
    fd.append("name", values.nom_produit);
    fd.append("slug", values.slug || "");
    fd.append("unit_id", values.id_unit ?? editingProduct?.unit_id ?? "");
    fd.append("category_id", values.id_categorie ?? "");
    fd.append("description", values.description ?? "");
    fd.append("barcode_symbology", values.barcode_symbology || "CODE128");
    fd.append(
      "stock_management_mode",
      values.stock_management_mode || "standard"
    );
    fd.append("tax_id", values.taxe ?? "");
    fd.append(
      "variant_attributes",
      JSON.stringify(buildVariantAttributes(variantAxes))
    );
    fd.append(
      "existing_variant_values",
      JSON.stringify(existingVariant.variant_values)
    );

    const variantsPayload = variantRows.map((variant) => ({
      variant_values: variant.variant_values,
      label: variant.label,
      item_code:
        variant.key === existingVariantKey
          ? undefined
          : variant.item_code
          ? String(variant.item_code).trim()
          : undefined,
      barcode_value:
        variant.key === existingVariantKey
          ? undefined
          : variant.barcode_value
          ? String(variant.barcode_value).trim()
          : undefined,
      purchase_price: variant.purchase_price ?? 0,
      sales_price: variant.sales_price ?? 0,
      tax_id: variant.tax_id ?? values.taxe ?? undefined,
      opening_stock:
        variant.key === existingVariantKey
          ? undefined
          : variant.opening_stock ?? 0,
      stock_quantitiy_alert: variant.stock_quantitiy_alert ?? 0,
    }));
    fd.append("variants", JSON.stringify(variantsPayload));

    if (values.image?.[0]?.originFileObj) {
      fd.append("image", values.image[0].originFileObj);
    }
    variantRows.forEach((variant, index) => {
      if (variant.key !== existingVariantKey && variant.imageFile) {
        fd.append(`variant_image_${index}`, variant.imageFile);
      }
    });

    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: "Convertir définitivement ce produit ?",
        content:
          "Le produit actuel sera conservé comme déclinaison avec son stock et son historique. Un nouveau parent de regroupement sera créé. La reconversion automatique en produit simple n'est pas disponible.",
        okText: "Convertir en variantes",
        cancelText: "Annuler",
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/produits/${editingProduct.id}/convert-to-variants`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      void invalidateProducts();
      message.success(
        `Produit converti avec succès (${response.data?.variants?.length || variantRows.length} déclinaison(s)). Le stock et l'historique existants ont été conservés.`
      );
      handleModalClose();
      fetchProducts();
    } catch (error) {
      console.error("[handleConvertToVariants] Erreur:", error);
      const data = error.response?.data;
      message.error(
        data?.error ||
          data?.details ||
          "Erreur lors de la conversion du produit en variantes"
      );
    }
  };

  const handleSubmit = async (values) => {
    if (productSubmitLockRef.current || subscriptionWriteRestricted || !hasPermission(
      `Gestion Commerciale.Produits.Produits.${editingProduct ? "edit" : "create"}`)) return;

    productSubmitLockRef.current = true;
    setProductSubmitting(true);
    let formData;

    try {
      const contextAtSubmit = productContextKey;
      const fieldsAtSubmit = JSON.stringify(form.getFieldsValue(true));
      const extraDraftAtSubmit = productDraftRef.current;
      let currentOptions;
      try {
        currentOptions = await productFormOptions.refresh({ force: true });
      } catch (error) {
        message.error("Impossible de revérifier les choix du formulaire. Votre saisie est conservée ; réessayez.");
        return;
      }
      if (!productContextRef.current.open || productContextRef.current.key !== contextAtSubmit ||
          fieldsAtSubmit !== JSON.stringify(form.getFieldsValue(true)) || extraDraftAtSubmit !== productDraftRef.current) {
        message.warning("Le contexte ou la saisie a changé pendant la vérification. Vérifiez le formulaire avant d’enregistrer.");
        return;
      }
      const conflicts = getProductReferenceConflicts(values, currentOptions);
      if (conflicts.length) {
        form.setFields(conflicts.map((item) => ({ name: item.field, errors: [item.message] })));
        focusProductField(conflicts[0].field);
        message.error(conflicts[0].message);
        return;
      }
      if (variantMode) {
        if (editingProduct) {
          await handleConvertToVariants(values);
        } else {
          await handleSubmitVariants(values);
        }
        return;
      }

      formData = new FormData();
      const resolvedUnitId = values.id_unit ?? editingProduct?.unit_id;

      const warehouseId = Number(values.warehouse);
      const selectedWarehouseLocal = currentOptions.warehouses.find(
        (w) => w.id === warehouseId
      );

      if (!selectedWarehouseLocal) {
        const warehouseErrorMessage = "Établissement non trouvé";
        form.setFields([
          {
            name: "warehouse",
            errors: [warehouseErrorMessage],
          },
        ]);
        focusProductField("warehouse");
        message.error(warehouseErrorMessage);
        return;
      }

      const mappedValues = {
        company_id: selectedWarehouseLocal.company_id,
        warehouse_id: warehouseId,
        product_type:
          values.product_type === "Produits finis" ? "single" : "raw",
        name: values.nom_produit,
        slug: values.slug,
        barcode_symbology: values.barcode_symbology,
        item_code: values.item_code,
        barcode_value: values.barcode_value || values.item_code,
        category_id: values.id_categorie,
        unit_id: resolvedUnitId,
        current_stock: values.quantite_stock,
        purchase_price: values.prix_achat,
        sales_price: values.prix_vente,
        tax_id: values.taxe,
        stock_quantitiy_alert: values.alerte_quantite,
        stock_management_mode: values.stock_management_mode || "standard",
      };

      formData.append("company_id", mappedValues.company_id);
      formData.append("warehouse_id", mappedValues.warehouse_id);
      formData.append("product_type", mappedValues.product_type);
      formData.append("name", values.nom_produit);
      formData.append("slug", values.slug);
      formData.append("barcode_symbology", values.barcode_symbology);
      formData.append("item_code", values.item_code);
      formData.append(
        "barcode_value",
        values.barcode_value || values.item_code || ""
      );

      // Champs optionnels/nullables : envoyer même si vide/null pour permettre la suppression
      formData.append("category_id", values.id_categorie ?? "");
      formData.append("unit_id", resolvedUnitId ?? "");
      formData.append("current_stock", values.quantite_stock);
      formData.append("purchase_price", values.prix_achat);
      formData.append("sales_price", values.prix_vente);
      formData.append("tax_id", values.taxe ?? "");
      formData.append("alert_quantity", values.alerte_quantite);
      formData.append(
        "stock_management_mode",
        values.stock_management_mode || "standard"
      );
      formData.append(
        "assistant_packaging_label",
        values.assistant_packaging_label ?? ""
      );
      formData.append(
        "assistant_packaging_quantity",
        values.assistant_packaging_quantity ?? ""
      );
      formData.append(
        "assistant_packaging_purchase_price",
        values.assistant_packaging_purchase_price ?? ""
      );
      formData.append(
        "purchase_packaging_profiles",
        JSON.stringify(
          buildPurchasePackagingProfilesPayload(purchasePackagingProfiles)
        )
      );

      // Description : toujours envoyer, même si vide, pour permettre l'effacement
      formData.append("description", values.description ?? "");

      if (values.image && values.image[0] && values.image[0].originFileObj) {
        formData.append("image", values.image[0].originFileObj);
      }

      if (editingProduct) {
        await axios.put(
          `${API_URL}/api/produits/${editingProduct.id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        void invalidateProducts();
        message.success("Produit modifié avec succès");
      } else {
        await axios.post(`${API_URL}/api/produits`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        void invalidateProducts();
        message.success("Produit ajouté avec succès");
      }

      handleModalClose();
      fetchProducts();
    } catch (error) {
      console.error("[handleSubmit] Erreur:", error);

      const errorData = error.response?.data;

      // *** NOUVEAU : Gestion produit supprimé existant ***
      if (
        error.response?.status === 409 &&
        errorData?.error === "DELETED_PRODUCT_EXISTS"
      ) {
        // Stocker les informations du produit supprimé et le FormData en attente
        setDeletedProductInfo(errorData.deletedProduct);
        setPendingFormData(formData);
        setDeletedProductModalVisible(true);
        return; // Arrêter le traitement, attendre la décision de l'utilisateur
      }

      // *** AMÉLIORATION UX : Gestion d'erreur détaillée avec focus automatique ***
      if (errorData?.field) {
        // Erreur sur un champ spécifique (doublon nom ou code article)
        const fieldName = errorData.field; // "nom_produit" ou "item_code"

        // Définir l'erreur sur le champ concerné
        form.setFields([
          {
            name: fieldName,
            errors: [errorData.error],
          },
        ]);

        // Focus automatique sur le champ en erreur
        focusProductField(fieldName);

        // Notification visuelle
        message.error({
          content: errorData.error,
          duration: 5,
          key: "product-error",
        });
      } else if (errorData?.error) {
        // Erreur générique mais structurée
        message.error({
          content: errorData.error,
          duration: 5,
        });
      } else {
        // Erreur réseau ou inconnue
        message.error("Erreur lors de l'enregistrement du produit");
      }
    } finally {
      productSubmitLockRef.current = false;
      setProductSubmitting(false);
    }
  };

  // --- Restaurer un produit supprimé avec les nouvelles données ---
  const handleRestoreDeletedProduct = async () => {
    if (!deletedProductInfo || !pendingFormData) return;

    try {
      setActionLoading(true);

      await axios.post(
        `${API_URL}/api/produits/restore-deleted/${deletedProductInfo.id}`,
        pendingFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      message.success("Produit supprimé restauré et mis à jour avec succès");
      void invalidateProducts();

      // Réinitialiser les états
      setDeletedProductModalVisible(false);
      setDeletedProductInfo(null);
      setPendingFormData(null);
      setModalVisible(false);
      form.resetFields();
      fetchProducts();
    } catch (error) {
      console.error("[handleRestoreDeletedProduct] Erreur:", error);
      message.error(
        error.response?.data?.error || "Erreur lors de la restauration du produit"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // --- Remplacer un produit supprimé par un nouveau ---
  const handleReplaceDeletedProduct = async () => {
    if (!deletedProductInfo || !pendingFormData) return;

    try {
      setActionLoading(true);

      const response = await axios.post(
        `${API_URL}/api/produits/replace-deleted/${deletedProductInfo.id}`,
        pendingFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      void invalidateProducts();

      message.success(
        `Ancien produit marqué comme définitivement supprimé. Nouveau produit créé (ID: ${response.data.newProductId})`
      );

      // Réinitialiser les états
      setDeletedProductModalVisible(false);
      setDeletedProductInfo(null);
      setPendingFormData(null);
      setModalVisible(false);
      form.resetFields();
      fetchProducts();
    } catch (error) {
      console.error("[handleReplaceDeletedProduct] Erreur:", error);
      message.error(
        error.response?.data?.error || "Erreur lors du remplacement du produit"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Update warehouses list when selectedCompany changes
  useEffect(() => {
    if (selectedCompany) {
      fetchWarehouses();
    } else {
      setWarehouses([]);
    }
  }, [selectedCompany]);

  // Auto-select first warehouse if none is selected (FIX: visibilité produits après import)
  useEffect(() => {
    if (warehouses.length > 0 && !selectedWarehouse) {
      const firstWarehouse = warehouses[0];
      setSelectedWarehouse(firstWarehouse.id);
      console.log('[GestionProduit] Auto-sélection warehouse:', firstWarehouse.name, '(ID:', firstWarehouse.id + ')');
    }
  }, [warehouses, selectedWarehouse, setSelectedWarehouse]);

  // Afficher un indicateur de chargement pendant l'initialisation
  if (isInitializing) {
    return (
      <div className="gestion-produit-container p-sm md:p-md">
        <div className="flex justify-center items-center h-64">
          <Spin size="large" tip="Initialisation..." />
        </div>
      </div>
    );
  }

  // Afficher un message si aucun magasin n'est sélectionné
  if (!selectionsReady) {
    return (
      <div className="gestion-produit-container p-sm md:p-md">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun établissement sélectionné
          </h3>
          <p className="text-gray-500 max-w-md">
            Veuillez sélectionner un établissement dans la barre de navigation pour
            afficher les produits.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestion-produit-container p-sm md:p-md">
      <div className="header-section flex flex-col md:flex-row md:justify-between md:items-center gap-md mb-md p-md shadow rounded-lg" style={{ backgroundColor: "var(--bg-primary)" }}>
        <div className="button-group flex flex-wrap items-center gap-sm">
          <Space size="middle">
            {hasPermission("Gestion Commerciale.Produits.Produits.create") && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreateProductModal}
                disabled={subscriptionWriteRestricted}
                title={
                  subscriptionWriteRestricted
                    ? subscriptionRestrictionTitle
                    : undefined
                }
              >
                Nouveau Produit
              </Button>
            )}
            {isCatalogPackEligible &&
              hasPermission("Gestion Commerciale.Produits.Produits.create") && (
                <Button
                  type="default"
                  icon={<PlusOutlined />}
                  onClick={() => setCatalogPackVisible(true)}
                  disabled={subscriptionWriteRestricted}
                  title={
                    subscriptionWriteRestricted
                      ? subscriptionRestrictionTitle
                      : undefined
                  }
                >
                  Initialiser le catalogue
                </Button>
              )}
            {hasPermission("Gestion Commerciale.Produits.Produits.import") && (
              <Button
                type="default"
                icon={<ImportOutlined />}
                onClick={handleImport}
                disabled={subscriptionWriteRestricted}
                title={
                  subscriptionWriteRestricted
                    ? subscriptionRestrictionTitle
                    : undefined
                }
              >
                Importer les Produits
              </Button>
            )}
            {hasPermission("Gestion Commerciale.Produits.Produits.export") && (
              <Dropdown.Button
                type="default"
                icon={<DownOutlined />}
                overlay={exportMenu}
                loading={exporting}
                onClick={() => handleExportProducts("csv")}
              >
                <ExportOutlined style={{ marginRight: 8 }} />
                Exporter
              </Dropdown.Button>
            )}
          </Space>
        </div>
        <div className="filter-section flex flex-wrap items-center gap-sm md:gap-md mt-sm md:mt-0">
          <Input
            placeholder="Rechercher un produit..."
            allowClear
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            className="w-full sm:w-auto md:max-w-xs lg:max-w-sm"
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder={
              !selectedCompany
                ? "Sélectionnez d'abord une entreprise"
                : categories.length === 0
                ? "Aucune catégorie disponible"
                : "Filtrer par catégorie"
            }
            allowClear
            className="w-full sm:w-auto md:min-w-[180px] lg:min-w-[200px]"
            onChange={handleCategoryFilter}
            disabled={!selectedCompany || categories.length === 0}
            notFoundContent={
              !selectedCompany
                ? "Veuillez sélectionner une entreprise"
                : "Aucune catégorie trouvée"
            }
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filtrer par type"
            allowClear
            className="w-full sm:w-auto md:min-w-[180px] lg:min-w-[200px]"
            value={filters.type || undefined}
            onChange={handleProductTypeFilter}
          >
            {/* Filtrage par type de produit */}
            <Option value="single">Produits finis</Option>
            <Option value="raw">
              {productionTerminology.materialPluralTitle}
            </Option>
            <Option value="production">
              {productionTerminology.unitsTitle}
            </Option>
          </Select>
          <Select
            placeholder="Filtrer par statut"
            allowClear
            className="w-full sm:w-auto md:min-w-[160px] lg:min-w-[180px]"
            value={filters.status}
            onChange={handleStatusFilter}
          >
            <Option value="all">Tous les statuts</Option>
            <Option value="actif">Actifs</Option>
            <Option value="disabled">Désactivés</Option>
            <Option value="archived">Archivés</Option>
            <Option value="deleted">Supprimés</Option>
          </Select>
        </div>
      </div>

      <Table
        columns={orderedColumns}
        dataSource={products}
        locale={{
          triggerAsc: "Cliquez pour trier par ordre croissant",
          triggerDesc: "Cliquez pour trier par ordre décroissant",
          cancelSort: "Cliquez pour annuler le tri",
        }}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: true }}
        expandable={{
          rowExpandable: (record) =>
            Boolean(record.is_variant_parent) || Number(record.variants_count) > 0,
          onExpand: (expanded, record) => {
            if (expanded) loadVariantChildren(record.id);
          },
          expandedRowRender: (record) => (
            <Table
              size="small"
              rowKey={(row) => `${row.id}-${row.warehouse_id ?? "x"}`}
              loading={Boolean(loadingVariantChildren[record.id])}
              pagination={false}
              dataSource={variantChildrenMap[record.id] || []}
              locale={{ emptyText: "Aucune déclinaison" }}
              columns={[
                {
                  title: "Déclinaison",
                  dataIndex: "variant_label",
                  render: (label) => <Tag color="blue">{label || "—"}</Tag>,
                },
                { title: "SKU", dataIndex: "item_code" },
                { title: "Code-barres", dataIndex: "barcode_value" },
                { title: "Établissement", dataIndex: "warehouse_nom" },
                {
                  title: "Stock",
                  dataIndex: "current_stock",
                  render: (v) => formatNumber(v ?? 0),
                },
                {
                  title: "Prix de vente",
                  dataIndex: "sales_price",
                  render: (v) =>
                    v !== null && v !== undefined ? `${formatNumber(v)} CFA` : "—",
                },
              ]}
            />
          ),
        }}
        onRow={(record) => ({
          onClick: (event) => {
            if (!canViewProductDetails || isInteractiveRowElement(event.target)) {
              return;
            }
            handleView(record);
          },
          style: {
            cursor: canViewProductDetails ? "pointer" : "default",
          },
        })}
      />

      <div className="pagination-container">
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page, pageSize) =>
            setPagination({ ...pagination, current: page, pageSize })
          }
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} sur ${total} produits`
          }
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <VariantManagerModal
        open={Boolean(manageVariantsParentId)}
        parentId={manageVariantsParentId}
        onClose={() => setManageVariantsParentId(null)}
        categories={categories}
        taxes={taxes}
        onSuccess={() => {
          void invalidateProducts();
          // Vider le cache des declinaisons du parent concerne pour forcer un
          // rechargement frais a la prochaine expansion, puis recharger la liste.
          if (manageVariantsParentId) {
            setVariantChildrenMap((prev) => {
              const next = { ...prev };
              delete next[manageVariantsParentId];
              return next;
            });
          }
          fetchProducts();
        }}
      />

      <ProductEditorModal
        ref={productEditorRef}
        form={form} open={modalVisible} editingProduct={editingProduct}
        activeTab={activeProductFormTab} onTabChange={setActiveProductFormTab}
        onClose={handleModalClose} onSubmit={handleSubmit} onFinishFailed={handleProductFormFinishFailed}
        variantMode={variantMode} canConfigureVariants={canConfigureVariants} onToggleVariants={handleToggleVariantMode}
        subscriptionWriteRestricted={subscriptionWriteRestricted} submitting={productSubmitting}
        options={{ warehouses: formOptionData?.warehouses || availableProductWarehouses,
          categories: formOptionData?.categories || [], units: formOptionData?.units || [],
          taxes: formOptionData?.taxes || [], companyId: selectedCompanyId,
          verified: productFormOptions.status === "verified", loading: productFormOptions.loading,
          rawMaterialLabel: productionTerminology.materialPluralTitle }}
        referenceConflicts={productReferenceConflicts}
        referencesNotice={productFormOptions.error ? <Alert type="warning" showIcon
          message="Actualisation des choix impossible"
          description={formOptionData ? "Les choix précédents et votre saisie sont conservés. Réessayez avant d’enregistrer." : "Les choix sont indisponibles. Votre saisie est conservée."}
          action={<Button onClick={() => productFormOptions.refresh({ force: true }).catch(() => {})}>Réessayer</Button>} />
          : productReferenceConflicts.length > 0 ? <Alert type="error" showIcon
            message="Une référence sélectionnée n’est plus disponible"
            description={productReferenceConflicts.map((item) => item.message).join(" ")} /> : null}
        actions={{ canCreateCategory: hasPermission("Gestion Commerciale.Produits.Categories.create"),
          canCreateUnit: hasPermission("Gestion Commerciale.Produits.Unites.create"),
          createCategory: openQuickCategoryModal, createUnit: openQuickUnitModal,
          checkProductExists, generateBarcode, showBarcode,
          changeWarehouse: (value) => {
            const warehouse = (formOptionData?.warehouses || availableProductWarehouses).find((item) => String(item.id) === String(value));
            setFormSelectedWarehouseCompanyId(warehouse?.company_id || null);
          },
          changeName: (name) => {
            if (name) form.setFieldsValue({ slug: generateSlug(name),
              ...(!(editingProduct && variantMode) ? { item_code: generateArticleCode(name) } : {}) });
          },
          changeBarcode: (value) => {
            form.setFieldsValue({ barcode_value: value }); setCurrentBarcode(value || null);
            if (!value && barcodeGenerated) setBarcodeGenerated(false);
          } }}
        formatting={{ quantity: formatQuantityForInput, price: formatPriceForInput,
          parseQuantity: parseQuantityInput, parsePrice: parsePriceInput }}
        assistantNotice={productionAssistantCreateMode && (
              <Alert
                showIcon
                type="info"
                message={productionTerminology.configurationSingularTitle}
                description={
                  <Space direction="vertical" size={8}>
                    <Text>
                      Créez ici vos {productionTerminology.materialPlural} avec leur unité de stock de
                      référence. Cette unité sera ensuite réutilisée dans les
                      {" "}{productionTerminology.configurationPlural}.
                    </Text>
                    <Text type="secondary">
                      Utilisez ensuite l'étape de préparation de{" "}
                      {productionTerminology.materialDefiniteSingular} pour
                      convertir vos conditionnements d'achat en stock et en prix
                      unitaires.
                    </Text>
                    <Space wrap>
                      <Button
                        type="default"
                        onClick={() =>
                          navigate("/production/units?assistant=production-setup")
                        }
                      >
                        Retour à l'assistant
                      </Button>
                      <Button
                        type="link"
                        onClick={() => setActiveProductFormTab("2")}
                      >
                        Aller à l'onglet Stock & Prix
                      </Button>
                    </Space>
                  </Space>
                }
                style={{ marginBottom: "16px" }}
              />
            )}
        catalogSuggestions={!editingProduct &&
                isCatalogPackEligible &&
                (loadingCatalogSuggestions ||
                  productCatalogSuggestions.length > 0) && (
                  <div className="mb-md">
                    <Alert
                      type="info"
                      showIcon
                      message="Produits standards détectés"
                      description={
                        loadingCatalogSuggestions ? (
                          <Spin size="small" />
                        ) : (
                          <Space
                            direction="vertical"
                            size={6}
                            style={{ width: "100%" }}
                          >
                            {productCatalogSuggestions.map((s, idx) => (
                              <div
                                key={`${s.name}-${idx}`}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span>
                                  <strong>{s.name}</strong>{" "}
                                  <Tag>
                                    {s.product_type === "single"
                                      ? "Produit fini"
                                      : productionTerminology.materialSingularTitle}
                                  </Tag>
                                  {s.unit_short_name ? (
                                    <Tag color="blue">{s.unit_short_name}</Tag>
                                  ) : null}
                                  <span style={{ color: "#888" }}>
                                    {` achat ${s.purchase_price} / vente ${s.sales_price}`}
                                  </span>
                                  {s.already_exists ? (
                                    <Tag color="orange">déjà présent</Tag>
                                  ) : null}
                                </span>
                                <Button
                                  size="small"
                                  type="primary"
                                  ghost
                                  onClick={() => applyCatalogProductSuggestion(s)}
                                >
                                  Utiliser
                                </Button>
                              </div>
                            ))}
                          </Space>
                        )
                      }
                    />
                  </div>
                )}
        stockExtras={<>
          {productionAssistantCreateMode && (
                  <div className="form-section">
                    <div className="form-section-header">
                      <div>
                        <h3 className="section-title">
                          Préparer {productionTerminology.materialDefiniteSingular} pour la {productionTerminology.costContext}
                        </h3>
                        <p className="section-subtitle">
                          Traduisez ici votre conditionnement d'achat habituel
                          vers l'unité de stock qui sera consommée dans la {productionTerminology.configurationSingular}.
                        </p>
                      </div>
                    </div>

                    <Alert
                      showIcon
                      type={assistantStockUnit ? "info" : "warning"}
                      message={
                        assistantStockUnit
                          ? `Unite de stock retenue: ${assistantStockUnitLabel}`
                          : "Choisissez d'abord l'unite de stock"
                      }
                      description={
                        assistantStockUnit
                          ? "Renseignez maintenant le conditionnement d'achat, puis laissez l'assistant calculer le prix unitaire et les quantites de stock."
                          : "La conversion se fait toujours vers l'unite de stock definie dans la section precedente."
                      }
                      style={{ marginBottom: "16px" }}
                    />

                    <div className="form-row grid grid-cols-1 md:grid-cols-3 gap-x-md gap-y-sm mb-md">
                      <Form.Item
                        name="assistant_packaging_label"
                        label="Conditionnement d'achat"
                        extra="Ex: Sac farine 25 kg, Carton 12 bouteilles, Bidon 5 L"
                      >
                        <Input placeholder="Nom du conditionnement habituel" />
                      </Form.Item>
                      <Form.Item
                        name="assistant_packaging_quantity"
                        label="Contenu d'un conditionnement"
                        extra={
                          assistantStockUnit
                            ? `Quantite exprimee en ${assistantStockUnitShortName}`
                            : "Choisissez d'abord l'unite de stock"
                        }
                      >
                        <InputNumber
                          className="w-full"
                          min={0}
                          step={0.01}
                          precision={4}
                          formatter={(value) => formatQuantityForInput(value, true)}
                          parser={(value) => parseQuantityInput(value, true)}
                          placeholder="Ex: 25"
                        />
                      </Form.Item>
                      <Form.Item
                        name="assistant_packaging_purchase_price"
                        label="Prix d'achat du conditionnement"
                        extra="Montant paye pour un conditionnement complet"
                      >
                        <InputNumber
                          className="w-full"
                          min={0}
                          step={0.01}
                          precision={4}
                          formatter={(value) => formatPriceForInput(value, true)}
                          parser={(value) => parsePriceInput(value, true)}
                          placeholder="Ex: 12500"
                        />
                      </Form.Item>
                    </div>

                    <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-x-md gap-y-sm mb-md">
                      <Form.Item
                        name="assistant_opening_package_count"
                        label="Stock initial en conditionnements"
                        extra="Facultatif. Sert a pre-remplir le stock initial canonique"
                      >
                        <InputNumber
                          className="w-full"
                          min={0}
                          step={0.01}
                          precision={4}
                          formatter={(value) => formatQuantityForInput(value, true)}
                          parser={(value) => parseQuantityInput(value, true)}
                          placeholder="Ex: 3"
                        />
                      </Form.Item>
                      <Form.Item
                        name="assistant_alert_package_count"
                        label="Seuil d'alerte en conditionnements"
                        extra="Facultatif. Traduit un seuil terrain vers le seuil de stock"
                      >
                        <InputNumber
                          className="w-full"
                          min={0}
                          step={0.01}
                          precision={4}
                          formatter={(value) => formatQuantityForInput(value, true)}
                          parser={(value) => parseQuantityInput(value, true)}
                          placeholder="Ex: 0,5"
                        />
                      </Form.Item>
                    </div>

                    <Alert
                      showIcon
                      type={
                        assistantCanApplyUnitPurchasePrice ||
                        assistantCanApplyOpeningStock ||
                        assistantCanApplyAlertQuantity
                          ? "success"
                          : "info"
                      }
                      message="Resume de conversion"
                      description={
                        <Space direction="vertical" size={8} style={{ width: "100%" }}>
                          <Text>
                            {assistantStockUnit
                              ? `1 ${assistantConditioningDisplayName} = ${
                                  assistantPackagingQuantityValue > 0
                                    ? `${formatAssistantValue(
                                        assistantPackagingQuantityValue
                                      )} ${assistantStockUnitShortName}`
                                    : `... ${assistantStockUnitShortName}`
                                }`
                              : "Selectionnez une unite de stock pour activer la conversion."}
                          </Text>
                          <Text>
                            {assistantCanApplyUnitPurchasePrice
                              ? `Prix unitaire calcule: ${formatAssistantValue(
                                  assistantDerivedUnitPurchasePrice
                                )} CFA / ${assistantStockUnitShortName} (actuel: ${formatAssistantValue(
                                  watchedPurchasePrice || 0
                                )} CFA / ${assistantStockUnitShortName})`
                              : "Renseignez le contenu et le prix du conditionnement pour calculer le prix unitaire."}
                          </Text>
                          <Text>
                            {assistantCanApplyOpeningStock
                              ? `Stock initial suggere: ${formatAssistantValue(
                                  assistantDerivedOpeningStock
                                )} ${assistantStockUnitShortName} (actuel: ${formatAssistantValue(
                                  watchedOpeningStockQuantity || 0
                                )} ${assistantStockUnitShortName})`
                              : "Ajoutez un nombre de conditionnements en stock si vous voulez pre-remplir le stock initial."}
                          </Text>
                          <Text>
                            {assistantCanApplyAlertQuantity
                              ? `Seuil suggere: ${formatAssistantValue(
                                  assistantDerivedAlertQuantity
                                )} ${assistantStockUnitShortName} (actuel: ${formatAssistantValue(
                                  watchedAlertQuantity || 0
                                )} ${assistantStockUnitShortName})`
                              : "Ajoutez un seuil en conditionnements si vous voulez pre-remplir l'alerte de stock."}
                          </Text>
                          <Space wrap>
                            <Button
                              type="default"
                              onClick={applyAssistantUnitPurchasePrice}
                              disabled={!assistantCanApplyUnitPurchasePrice}
                            >
                              Appliquer au prix d'achat
                            </Button>
                            <Button
                              type="default"
                              onClick={applyAssistantOpeningStock}
                              disabled={!assistantCanApplyOpeningStock}
                            >
                              Appliquer au stock initial
                            </Button>
                            <Button
                              type="default"
                              onClick={applyAssistantAlertQuantity}
                              disabled={!assistantCanApplyAlertQuantity}
                            >
                              Appliquer a l'alerte
                            </Button>
                            {!assistantStockUnit && (
                              <Button type="link" onClick={openQuickUnitModal}>
                                Creer l'unite de stock
                              </Button>
                            )}
                          </Space>
                        </Space>
                      }
                      style={{ marginBottom: "8px" }}
                    />
                  </div>
                )}
          {showPurchasePackagingProfilesSection && (
                  <div className="form-section">
                    <div className="form-section-header">
                      <div>
                        <h3 className="section-title">
                          Conditionnements d’achat
                        </h3>
                        <p className="section-subtitle">
                          Facultatif · Les contenus sont exprimés dans l’unité de stock
                          {assistantStockUnit ? ` : ${assistantStockUnitShortName}.` : " sélectionnée."}
                        </p>
                      </div>
                      <Button type="default" icon={<PlusOutlined />} onClick={addEmptyPurchasePackagingProfile}
                        disabled={!assistantStockUnit}>Ajouter un profil</Button>
                    </div>

                    {!assistantStockUnit && <Alert
                      showIcon
                      type={assistantStockUnit ? "info" : "warning"}
                      message={
                        assistantStockUnit
                          ? `Tous les profils seront rattaches a l'unite de stock ${assistantStockUnitLabel}.`
                          : "Choisissez d’abord l’unité de stock."
                      }
                      description={
                        assistantStockUnit
                          ? "Si vous changez l'unite de stock, les conditionnements devront etre verifies pour conserver des conversions coherentes."
                          : `Les conditionnements d'achat sont toujours exprimés dans l'unité de stock de ${productionTerminology.materialDefiniteSingular}.`
                      }
                      style={{ marginBottom: 16 }}
                    />}

                    {productionAssistantCreateMode && <Space wrap style={{ marginBottom: 16 }}>
                      <Button
                        type="default"
                        onClick={() => saveAssistantPackagingProfile(false)}
                        disabled={!canSaveAssistantPackagingProfile}
                      >
                        Ajouter le conditionnement saisi
                      </Button>
                      <Button
                        type="default"
                        onClick={() => saveAssistantPackagingProfile(true)}
                        disabled={!canSaveAssistantPackagingProfile}
                      >
                        Enregistrer comme profil par defaut
                      </Button>
                    </Space>}

                    {purchasePackagingProfiles.length ? (
                      <div style={{ display: "grid", gap: 12 }}>
                        {purchasePackagingProfiles.map((profile, index) => (
                          <div
                            key={profile.client_key || profile.id || index}
                            className="product-editor-packaging-profile"
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 12,
                                flexWrap: "wrap",
                              }}
                            >
                              <Space wrap>
                                <Text strong>{`Profil ${index + 1}`}</Text>
                                {profile.is_default ? (
                                  <Tag color="success">Par défaut</Tag>
                                ) : (
                                  <Button
                                    type="link"
                                    onClick={() =>
                                      setDefaultPurchasePackagingProfile(index)
                                    }
                                    style={{ paddingInline: 0 }}
                                  >
                                    Définir par défaut
                                  </Button>
                                )}
                              </Space>
                              <Space wrap>
                                {productionAssistantCreateMode && <Button
                                  type="link"
                                  onClick={() =>
                                    loadPackagingProfileIntoAssistant(index)
                                  }
                                  style={{ paddingInline: 0 }}
                                >
                                  Charger dans l'assistant
                                </Button>}
                                <Button
                                  type="text"
                                  danger
                                  icon={<MinusOutlined />}
                                  onClick={() => removePurchasePackagingProfile(index)}
                                >
                                  Supprimer
                                </Button>
                              </Space>
                            </div>

                            <div className="form-row grid grid-cols-1 md:grid-cols-3 gap-x-md gap-y-sm">
                              <Form.Item
                                label="Libellé du conditionnement"
                                htmlFor={`product-packaging-label-${index}`}
                                className="form-item--compact"
                              >
                                <Input
                                  id={`product-packaging-label-${index}`}
                                  value={profile.label}
                                  onChange={(event) =>
                                    updatePurchasePackagingProfile(
                                      index,
                                      "label",
                                      event.target.value
                                    )
                                  }
                                  placeholder="Ex: Sac 25 kg"
                                />
                              </Form.Item>
                              <Form.Item
                                label={`Contenu (${assistantStockUnitShortName})`}
                                htmlFor={`product-packaging-content-${index}`}
                                className="form-item--compact"
                              >
                                <InputNumber
                                  id={`product-packaging-content-${index}`}
                                  className="w-full"
                                  min={0}
                                  step={0.01}
                                  precision={4}
                                  formatter={(value) =>
                                    formatQuantityForInput(value, true)
                                  }
                                  parser={(value) =>
                                    parseQuantityInput(value, true)
                                  }
                                  value={profile.contained_quantity}
                                  onChange={(value) =>
                                    updatePurchasePackagingProfile(
                                      index,
                                      "contained_quantity",
                                      value
                                    )
                                  }
                                  placeholder="Ex: 25"
                                />
                              </Form.Item>
                              <Form.Item
                                label="Prix d’achat de référence"
                                htmlFor={`product-packaging-price-${index}`}
                                className="form-item--compact"
                              >
                                <InputNumber
                                  id={`product-packaging-price-${index}`}
                                  className="w-full"
                                  min={0}
                                  step={0.01}
                                  precision={4}
                                  formatter={(value) =>
                                    formatPriceForInput(value, true)
                                  }
                                  parser={(value) =>
                                    parsePriceInput(value, true)
                                  }
                                  value={profile.reference_purchase_price}
                                  onChange={(value) =>
                                    updatePurchasePackagingProfile(
                                      index,
                                      "reference_purchase_price",
                                      value
                                    )
                                  }
                                  placeholder="Ex: 12500"
                                />
                              </Form.Item>
                            </div>

                            <div className="product-editor-packaging-summary"><Text type="secondary">
                              {profile.contained_quantity > 0
                                ? `1 ${profile.label || "conditionnement"} = ${formatAssistantValue(
                                    profile.contained_quantity
                                  )} ${assistantStockUnitShortName}`
                                : "Renseignez le contenu pour rendre ce profil exploitable."}
                              {profile.reference_purchase_price > 0 &&
                                profile.contained_quantity > 0 &&
                                `, soit ${formatAssistantValue(
                                  profile.reference_purchase_price /
                                    profile.contained_quantity
                                )} CFA / ${assistantStockUnitShortName}.`}
                            </Text>
                            {index === 0 && <Text type="secondary">Vérifiez les conversions si vous changez l’unité de stock.</Text>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Text type="secondary">Aucun conditionnement enregistré. Vous pouvez créer la matière première sans profil.</Text>
                    )}
                  </div>
                )}
          {showProductionUsageProfilesSection && (
                  <div className="form-section">
                    <div className="form-section-header">
                      <div>
                        <h3 className="section-title">
                          Usages en {productionTerminology.costContext}
                        </h3>
                        <p className="section-subtitle">
                          Consultez les profils associés à {productionTerminology.materialDefiniteSingular} dans le module de {productionTerminology.costContext}.
                        </p>
                      </div>
                    </div>

                    <Table
                      size="small"
                      rowKey={(profile) => profile.id}
                      loading={loadingProductionUsageProfiles}
                      pagination={false}
                      dataSource={productionUsageProfiles}
                      columns={[
                        {
                          title: productionTerminology.configurationSingularTitle,
                          dataIndex: "production_unit_name",
                          key: "production_unit_name",
                          render: (value) => value || "-",
                        },
                        {
                          title: "Profil",
                          dataIndex: "label",
                          key: "label",
                          render: (value) => value || "-",
                        },
                        {
                          title: "Sortie de référence",
                          key: "reference_output",
                          render: (_, profile) =>
                            `${formatAssistantValue(
                              profile.reference_output_quantity
                            )} ${profile.reference_output_unit_short_name}`,
                        },
                        {
                          title: "Consommation",
                          key: "material_quantity",
                          render: (_, profile) =>
                            `${formatAssistantValue(
                              profile.material_quantity
                            )} ${profile.material_unit_short_name}`,
                        },
                        {
                          title: "Ajustement",
                          key: "adjustment",
                          render: (_, profile) => {
                            const adjustments = [];
                            if (profile.loss_rate > 0) {
                              adjustments.push(
                                `perte ${formatAssistantValue(profile.loss_rate)}%`
                              );
                            }
                            if (profile.yield_rate > 0) {
                              adjustments.push(
                                `rendement ${formatAssistantValue(profile.yield_rate)}%`
                              );
                            }
                            return adjustments.length
                              ? adjustments.join(", ")
                              : "-";
                          },
                        },
                      ]}
                      locale={{
                        emptyText: `Aucun usage en ${productionTerminology.costContext} pour ${productionTerminology.materialDefiniteSingular}.`,
                      }}
                    />
                  </div>
                )}
        </>}
        variantContent={<div className="form-container">
                  <VariantManager
                    axes={variantAxes}
                    setAxes={setVariantAxes}
                    variants={variantRows}
                    setVariants={setVariantRows}
                    getDefaults={() => ({
                      purchase_price:
                        Number(form.getFieldValue("prix_achat")) || 0,
                      sales_price:
                        Number(form.getFieldValue("prix_vente")) || 0,
                      stock_quantitiy_alert:
                        Number(form.getFieldValue("alerte_quantite")) || 0,
                      tax_id: form.getFieldValue("taxe") ?? null,
                    })}
                    existingProduct={
                      editingProduct
                        ? {
                            name: editingProduct.name,
                            item_code: editingProduct.item_code,
                            barcode_value:
                              editingProduct.barcode_value ||
                              editingProduct.item_code,
                            current_stock:
                              Number(editingProduct.current_stock) || 0,
                          }
                        : null
                    }
                    existingVariantKey={existingVariantKey}
                    onExistingVariantChange={setExistingVariantKey}
                  />
                </div>}
        galleryContent={editingProduct?.id ? <div style={{ padding: '16px' }}>
                  <Alert
                    message="Galerie multi-images"
                    description="Ajoutez jusqu'à 10 images supplémentaires pour améliorer l'affichage de vos produits en ligne."
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                  <ProductImageGallery
                    productId={editingProduct.id}
                    readOnly={subscriptionWriteRestricted}
                  />
                </div> : null}
        assistantFooter={productionAssistantCreateMode && (
                  <Button
                    type="default"
                    onClick={() =>
                      navigate("/production/units/create?assistant=production-setup")
                    }
                  >
                    Ouvrir : {productionTerminology.configurationSingularTitle}
                  </Button>
                )}
      />

      <Modal
        title="Nouvelle catégorie"
        open={quickCategoryModalVisible}
        onCancel={() => {
          setQuickCategoryModalVisible(false);
          quickCategoryForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={460}
      >
        <Form
          form={quickCategoryForm}
          layout="vertical"
          onFinish={handleQuickCategorySubmit}
        >
          <Form.Item
            name="name"
            label="Nom"
            rules={[
              { required: true, message: "Le nom de la catégorie est requis" },
              { min: 2, message: "Le nom doit contenir au moins 2 caractères" },
            ]}
            extra="Utilisez un libellé court et métier pour faciliter le classement."
          >
            <Input placeholder="Ex: Boissons" autoFocus />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            extra="Optionnelle, utile pour préciser le périmètre de la catégorie."
          >
            <Input.TextArea
              placeholder="Description courte de la catégorie"
              rows={3}
            />
          </Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <Button
              onClick={() => {
                setQuickCategoryModalVisible(false);
                quickCategoryForm.resetFields();
              }}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={quickCategoryLoading}
              disabled={subscriptionWriteRestricted}
            >
              Créer
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Nouvelle unité"
        open={quickUnitModalVisible}
        onCancel={() => {
          setQuickUnitModalVisible(false);
          quickUnitForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={460}
      >
        <Form
          form={quickUnitForm}
          layout="vertical"
          onFinish={handleQuickUnitSubmit}
          initialValues={{ base_unit: "" }}
        >
          {productionAssistantCreateMode && (
            <Alert
              showIcon
              type="info"
              message="Unite de stock canonique"
              description={`Créez ici uniquement l'unité utilisée dans les ${productionTerminology.configurationPlural}, par exemple kg, L ou pièce. Le conditionnement d'achat se renseigne ensuite dans l'assistant, sans créer une unité supplémentaire.`}
              style={{ marginBottom: "16px" }}
            />
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 14,
            }}
          >
            <Form.Item
              name="name"
              label="Nom"
              rules={[
                { required: true, message: "Le nom de l'unité est requis" },
                { min: 2, message: "Le nom doit contenir au moins 2 caractères" },
              ]}
              extra="Nom complet utilisé dans le catalogue produit."
            >
              <Input placeholder="Ex: Kilogramme" autoFocus />
            </Form.Item>
            <Form.Item
              name="short_name"
              label="Nom court"
              rules={[
                { required: true, message: "Le nom court est requis" },
                { max: 20, message: "Le nom court ne peut pas dépasser 20 caractères" },
              ]}
              extra="Abréviation affichée dans les sélecteurs et tableaux."
            >
              <Input placeholder="Ex: kg" />
            </Form.Item>
          </div>
          <Form.Item
            name="base_unit"
            label="Unité de base"
            extra="Optionnelle. Renseignez-la si cette unité dérive d'une unité racine."
          >
            <Input placeholder="Ex: g" />
          </Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <Button
              onClick={() => {
                setQuickUnitModalVisible(false);
                quickUnitForm.resetFields();
              }}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={quickUnitLoading}
              disabled={subscriptionWriteRestricted}
            >
              Créer
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Code-barres"
        open={barcodeModalVisible}
        onCancel={() => setBarcodeModalVisible(false)}
        footer={[
          <Button key="print" type="primary" onClick={printBarcode}>
            Imprimer
          </Button>,
          <Button key="close" onClick={() => setBarcodeModalVisible(false)}>
            Fermer
          </Button>,
        ]}
      >
        <div
          id="barcode-print"
          style={{ textAlign: "center", padding: "20px" }}
        >
          {currentBarcode && (
            <Barcode
              value={currentBarcode}
              format={form.getFieldValue("barcode_symbology")}
            />
          )}
        </div>
      </Modal>

      <Modal
        title="Importer des Produits par CSV"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setImportModalVisible(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isUploading}
            onClick={handleUpload}
            disabled={
              subscriptionWriteRestricted ||
              fileList.length === 0 ||
              isUploading
            }
          >
            {isUploading ? "Importation..." : "Lancer l'importation"}
          </Button>,
        ]}
        width={700}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Alert
            message="Instructions pour l'importation"
            description={
              <div>
                <p>
                  Utilisez cette fonctionnalité pour créer ou mettre à jour des
                  produits en masse via un fichier CSV.
                </p>
                <p>
                  Le fichier doit avoir les colonnes suivantes (respecter
                  l'ordre et les noms exacts) :<br />
                  <code>item_code</code> (unique, optionnel),{" "}
                  <code>barcode_value</code> (optionnel, distinct si besoin),{" "}
                  <code>name</code> (requis), <code>warehouse_name</code>{" "}
                  (requis),{" "}
                  <code>category_name</code> (requis),{" "}
                  <code>unit_short_name</code> (requis, ex: 'kg', 'pcs'),{" "}
                  <code>product_type</code> (requis: 'single' ou 'raw'),{" "}
                  <code>barcode_symbology</code> (requis: 'CODE128', 'EAN13',
                  'CODE39'), <code>purchase_price</code> (requis),{" "}
                  <code>sales_price</code> (requis),{" "}
                  <code>stock_quantitiy_alert</code> (requis),{" "}
                  <code>tax_name</code> (optionnel), <code>slug</code>{" "}
                  (optionnel, généré si vide), <code>description</code>{" "}
                  (optionnel), <code>opening_stock</code> (optionnel).
                </p>
                <p>
                  <Link onClick={downloadTemplate}>
                    <DownloadOutlined /> Télécharger le fichier template (.csv)
                  </Link>
                </p>
                <p>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  Si un <code>item_code</code> existe déjà, la ligne mettra à
                  jour le produit existant (infos générales) et les détails pour
                  l'établissement spécifié (prix, taxe, alerte). Si l'établissement n'est
                  pas encore lié au produit, il sera ajouté.
                </p>
                <p>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  Si <code>barcode_value</code> est vide, le système reprend
                  automatiquement <code>item_code</code> comme valeur scannable.
                </p>
                <p>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  La colonne <code>opening_stock</code> sert à définir le stock
                  initial
                  <em>uniquement</em> lors de la première création du produit ou
                  de son ajout à un établissement via cet import. Laisser vide ou 0
                  si non applicable. Elle ne modifie pas le stock des
                  associations produit-établissement déjà existantes.
                </p>
              </div>
            }
            type="info"
            showIcon
          />

          <Dragger
            name="importFile"
            multiple={false}
            accept=".csv"
            fileList={fileList}
            beforeUpload={(file) => {
              const isCsv =
                file.type === "text/csv" ||
                file.name.toLowerCase().endsWith(".csv");
              if (!isCsv) {
                message.error(`${file.name} n'est pas un fichier CSV.`);
              } else {
                setFileList([file]);
                setImportResult(null);
                message.success(`${file.name} sélectionné.`);
              }
              return false;
            }}
            onRemove={() => {
              setFileList([]);
              setImportResult(null);
            }}
            style={{ padding: "20px" }}
            disabled={subscriptionWriteRestricted || isUploading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              Cliquez ou glissez votre fichier .csv ici
            </p>
            <p className="ant-upload-hint">
              Sélectionnez un seul fichier CSV à importer.
            </p>
          </Dragger>

          {isUploading && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <Spin size="large" tip="Traitement du fichier en cours..." />
            </div>
          )}

          {importResult && !isUploading && (
            <div style={{ marginTop: 20 }}>
              <Title level={5}>Résultat de l'importation :</Title>
              {importResult.successCount > 0 && (
                <Alert
                  message={`${importResult.successCount} produit(s) importé(s)/mis à jour avec succès.`}
                  type="success"
                  showIcon
                  style={{ marginBottom: 10 }}
                />
              )}
              {importResult.errorCount > 0 && (
                <Alert
                  message={`${importResult.errorCount} erreur(s) lors de l'importation.`}
                  description={
                    <ul
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        paddingLeft: "20px",
                      }}
                    >
                      {importResult.errors?.map((err, index) => (
                        <li key={index}>
                          {err.row ? `Ligne ${err.row} ` : ""}
                          {err.itemCode && err.itemCode !== "N/A"
                            ? `(Code: ${err.itemCode}) `
                            : ""}
                          : {err.error}
                        </li>
                      ))}
                    </ul>
                  }
                  type="error"
                  showIcon
                />
              )}
            </div>
          )}
        </Space>
      </Modal>

      {/* Modal de gestion des produits supprimés (restauration/remplacement) */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: "#faad14" }} />
            <span>Produit Supprimé Détecté</span>
          </Space>
        }
        open={deletedProductModalVisible}
        onCancel={() => {
          setDeletedProductModalVisible(false);
          setDeletedProductInfo(null);
          setPendingFormData(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setDeletedProductModalVisible(false);
              setDeletedProductInfo(null);
              setPendingFormData(null);
            }}
          >
            Annuler
          </Button>,
          <Button
            key="restore"
            type="default"
            icon={<UndoOutlined />}
            onClick={handleRestoreDeletedProduct}
            loading={actionLoading}
            disabled={subscriptionWriteRestricted}
          >
            Restaurer l'ancien
          </Button>,
          <Button
            key="replace"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleReplaceDeletedProduct}
            loading={actionLoading}
            disabled={subscriptionWriteRestricted}
          >
            Créer un nouveau
          </Button>,
        ]}
        width={600}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Alert
            message="Un produit supprimé avec ce nom existe déjà dans cet établissement"
            description="Vous pouvez soit restaurer l'ancien produit avec les nouvelles données, soit créer un tout nouveau produit."
            type="warning"
            showIcon
          />

          {deletedProductInfo && (
            <div>
              <Title level={5}>Informations du produit supprimé :</Title>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text>
                  <strong>Nom :</strong> {deletedProductInfo.name}
                </Text>
                <Text>
                  <strong>Code article :</strong> {deletedProductInfo.item_code || "N/A"}
                </Text>
                <Text>
                  <strong>Code-barres :</strong>{" "}
                  {deletedProductInfo.barcode_value ||
                    deletedProductInfo.item_code ||
                    "N/A"}
                </Text>
                <Text>
                  <strong>Prix de vente :</strong>{" "}
                  {deletedProductInfo.sales_price
                    ? `${formatNumber(deletedProductInfo.sales_price)} F`
                    : "N/A"}
                </Text>
                <Text>
                  <strong>Prix d'achat :</strong>{" "}
                  {deletedProductInfo.purchase_price
                    ? `${formatNumber(deletedProductInfo.purchase_price)} F`
                    : "N/A"}
                </Text>
                <Text>
                  <strong>Stock actuel :</strong> {deletedProductInfo.current_stock || 0}
                </Text>
                <Text>
                  <strong>Supprimé le :</strong>{" "}
                  {deletedProductInfo.deleted_at
                    ? dayjs(deletedProductInfo.deleted_at).format("DD/MM/YYYY HH:mm")
                    : "N/A"}
                </Text>
              </Space>
            </div>
          )}

          <Alert
            message="Choisissez une action :"
            description={
              <ul style={{ paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
                <li>
                  <strong>Restaurer l'ancien :</strong> Le produit supprimé sera réactivé et
                  mis à jour avec vos nouvelles données (prix, description, image, etc.)
                </li>
                <li style={{ marginTop: 8 }}>
                  <strong>Créer un nouveau :</strong> L'ancien produit sera marqué comme
                  définitivement supprimé (pour traçabilité) et un nouveau produit avec un
                  ID différent sera créé
                </li>
              </ul>
            }
            type="info"
            showIcon
          />
        </Space>
      </Modal>

      {/* Modal de suppression sécurisée */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedProductForAction(null);
        }}
        onConfirm={(action, responseData) => {
          void invalidateProducts();
          // Le modal a déjà fait l'API call, on ne fait que la mise à jour locale
          setDeleteModalVisible(false);

          if (action === 'delete') {
            setProducts(prev => prev.filter(p => p.id !== selectedProductForAction.id));
          } else {
            // Pour les autres actions, recharger la liste
            fetchProducts();
          }

          setSelectedProductForAction(null);
        }}
        entity={selectedProductForAction}
        entityType="produit"
        entityName={selectedProductForAction?.name}
        loading={false}
        apiEndpoint={`${API_URL}/api/produits`}
      />

      <ProductDetailsModal
        open={Boolean(detailsTarget && detailsTarget.context === detailsContext)}
        canView={canViewProductDetails}
        productId={detailsTarget?.id}
        userId={user?.id}
        companyId={selectedCompanyId}
        warehouseId={selectedWarehouse}
        warehouseName={warehouses.find((warehouse) => Number(warehouse.id) === Number(selectedWarehouse))?.name}
        terminology={productionTerminology}
        onClose={() => setDetailsTarget(null)}
      />

      <ProductCatalogPackModal
        visible={catalogPackVisible}
        warehouseId={selectedWarehouse}
        companyId={selectedCompanyId}
        terminology={productionTerminology}
        warehouseName={
          warehouses.find(
            (w) => Number(w.id) === Number(selectedWarehouse)
          )?.name
        }
        onCancel={() => setCatalogPackVisible(false)}
        onApplied={() => {
          setCatalogPackVisible(false);
          fetchProducts();
        }}
      />
    </div>
  );
};

export default GestionProduit;
